import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import PropTypes from 'prop-types';
import { getSchemeCodeByName, getSchemeNameByCode } from '../FilterSidebar/utils/schemeMapping';

const makePopupDraggable = (popupElement) => {
  if (!popupElement) return;
  
  // Get the entire popup content as the drag handle
  const popupContent = popupElement.closest('.leaflet-popup-content');
  const popupContainer = popupElement.closest('.leaflet-popup');
  
  if (!popupContainer) return;
  
  let isDragging = false;
  let startX, startY, initialX, initialY;
  let dragTarget = popupContainer;

  const onMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    
    const newX = initialX + dx;
    const newY = initialY + dy;
    
    // Apply transform to the entire popup container
    dragTarget.style.transform = `translate(${newX}px, ${newY}px)`;
    dragTarget.classList.add('dragging');
    
    // Prevent text selection during drag
    document.body.style.userSelect = 'none';
  };

  const onMouseUp = () => {
    if (!isDragging) return;
    
    isDragging = false;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    
    document.body.style.cursor = 'default';
    document.body.style.userSelect = '';
    dragTarget.classList.remove('dragging');
  };

  const onMouseDown = (e) => {
    // Don't start drag if clicking on interactive elements
    if (e.target.tagName === 'BUTTON' || 
        e.target.tagName === 'INPUT' || 
        e.target.tagName === 'A' ||
        e.target.tagName === 'SELECT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.closest('button') ||
        e.target.closest('.tab-button') ||
        e.target.closest('.leaflet-popup-close-button') ||
        e.target.closest('[onclick]') ||
        e.target.closest('img') ||  // Allow clicking on images without dragging
        e.target.closest('.image-controls') ||  // Image control buttons
        e.target.closest('.modal-button')) {
      return;
    }
    
    e.preventDefault();
    
    isDragging = true;
    
    // Get current transform values
    const transform = dragTarget.style.transform;
    const match = transform.match(/translate\(([-\d.]+)px,\s*([-\d.]+)px\)/);
    
    if (match) {
      initialX = parseFloat(match[1]);
      initialY = parseFloat(match[2]);
    } else {
      initialX = 0;
      initialY = 0;
    }
    
    startX = e.clientX;
    startY = e.clientY;
    
    document.body.style.cursor = 'grabbing';
    
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Remove any existing listener
  if (popupContainer._dragListener) {
    popupContainer.removeEventListener('mousedown', popupContainer._dragListener);
  }
  
  // Add mousedown listener to the entire popup container
  popupContainer.addEventListener('mousedown', onMouseDown);
  popupContainer._dragListener = onMouseDown;
  
  // Add a visual indicator that the popup is draggable (subtle)
  popupContainer.style.cursor = 'default';
  
  // Store the cleanup function
  popupElement._dragCleanup = () => {
    if (popupContainer._dragListener) {
      popupContainer.removeEventListener('mousedown', popupContainer._dragListener);
    }
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };
};


const draggablePopupStyles = `
  <style>
    .leaflet-popup {
      transition: box-shadow 0.2s ease;
    }
    
    .leaflet-popup-content-wrapper {
      cursor: default;
      user-select: none;
      transition: all 0.2s ease;
    }
    
    .leaflet-popup-content {
      margin: 0 !important;
      min-width: 400px;
      cursor: default;
    }
    
    /* Make entire popup draggable with subtle visual feedback */
    .leaflet-popup.dragging {
      opacity: 0.98;
      z-index: 10000 !important;
      transition: none;
    }
    
    .leaflet-popup.dragging .leaflet-popup-content-wrapper {
      box-shadow: 0 20px 40px rgba(0,0,0,0.3);
      cursor: grabbing !important;
    }
    
    .leaflet-popup:not(.dragging):hover .leaflet-popup-content-wrapper {
      box-shadow: 0 8px 30px rgba(0,0,0,0.15);
      border: 1px solid rgba(102, 126, 234, 0.3);
    }
    
    /* Add a subtle gradient border to indicate draggable area */
    .leaflet-popup-content-wrapper {
      border: 1px solid transparent;
      background-clip: padding-box;
      position: relative;
    }
    
    .leaflet-popup-content-wrapper::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, #667eea, #764ba2);
      opacity: 0;
      transition: opacity 0.2s ease;
      border-radius: 8px 8px 0 0;
      pointer-events: none;
    }
    
    .leaflet-popup:hover .leaflet-popup-content-wrapper::before {
      opacity: 0.5;
    }
    
    .leaflet-popup.dragging .leaflet-popup-content-wrapper::before {
      opacity: 1;
      height: 4px;
    }
    
    /* Ensure interactive elements remain clickable */
    .leaflet-popup button,
    .leaflet-popup .tab-button,
    .leaflet-popup [onclick],
    .leaflet-popup input,
    .leaflet-popup select,
    .leaflet-popup textarea,
    .leaflet-popup a,
    .leaflet-popup img {
      cursor: pointer;
      position: relative;
      z-index: 10001;
      pointer-events: auto !important;
    }
    
    /* Make sure images don't trigger drag */
    .leaflet-popup img {
      pointer-events: auto !important;
      user-select: none;
      -webkit-user-drag: none;
    }
    
    /* Style for interactive elements to show they're clickable */
    .leaflet-popup .tab-button {
      cursor: pointer;
      transition: all 0.2s ease;
    }
    
    .leaflet-popup .tab-button:hover {
      transform: translateY(-1px);
    }
    
    /* Prevent text selection during drag */
    .leaflet-popup.dragging * {
      user-select: none !important;
      pointer-events: none !important;
    }
    
    /* But allow interactive elements to work when not dragging */
    .leaflet-popup:not(.dragging) * {
      pointer-events: auto;
    }
    
    /* Style for the close button */
    .leaflet-popup-close-button {
      cursor: pointer !important;
      z-index: 10002 !important;
    }
    
    /* Add a small grab indicator in the corner (optional) */
    .leaflet-popup-content::after {
      content: '⋮⋮';
      position: absolute;
      bottom: 8px;
      right: 12px;
      color: #ccc;
      font-size: 16px;
      letter-spacing: 2px;
      opacity: 0;
      transition: opacity 0.2s ease;
      pointer-events: none;
    }
    
    .leaflet-popup:hover .leaflet-popup-content::after {
      opacity: 0.3;
    }
    
    .leaflet-popup.dragging .leaflet-popup-content::after {
      opacity: 0.5;
      color: #667eea;
    }
  </style>
`;



delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom icon for surveyor locations
const createSurveyorIcon = (schemeCode) => {
  const color = getSchemeColor(schemeCode);
  
  return L.divIcon({
    className: 'surveyor-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 12px;
        height: 12px;
        border-radius: 50%;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        cursor: pointer;
      "></div>
    `,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
};

export const getSchemeColor = (schemeCode, subSchemeCode = null) => {
  const code = parseInt(schemeCode);
  
  if (code === 18 && subSchemeCode) {
    const subCode = parseInt(subSchemeCode);
    const acabcColors = {
      1: '#FFA07A', // ACABC - Agriventure - Light Salmon
      2: '#FFD700'  // ACABC - Nodal Training Institute - Gold
    };
    return acabcColors[subCode] || '#c5ed62ff';
  }
  
  const colorMap = {
    1: '#FF0000',  // PDMC - Red
    2: '#00CED1',  // Rainfed Area Development - Dark Turquoise
    3: '#1E90FF',  // Agroforestry - Dodger Blue
    5: '#FFD700',  // NMEO-OS - Gold
    6: '#9370DB',  // National Mission on Natural Farming (NMNF)
    7: '#FFA500',  // NBHM - Orange
    8: '#20B2AA',  // MIDH - Light Sea Green
    9: '#FFD700',  // MOVCD - Gold
    10: '#DA70D6', // CDB - Orchid
    11: '#87CEFA', // DASD - Light Sky Blue
    12: '#FFA500', // GT - Orange
    13: '#98FB98', // Crop Health - Pale Green
    14: '#FF6347', // NMEO-OP - Tomato
    15: '#4682B4', // RKVY DPR - Steel Blue
    17: '#D2691E', // DCCD - Chocolate
    18: '#9ACD32', // ACABC default - Yellow Green
    21: '#32CD32', // National Bamboo Mission - Lime Green
    22: '#FF6347', // PKVY - Tomato
    23: '#FF6347', // PDMC-API
    24: '#FF4500' , // Agri Infra Fund - Orange Red
    27: '#4682B4', //"MT-SMAM"
    29: '#FF6347', // Seed Hub
    40: '#FF6347',// "MT-CRM"
    41: '#1E90FF', // NFSNM (SD)
    42: '#DA70D6 ',// NFSNM (CFLD)
    43: '#87CEFA', // NFSNM (FLD)
    53: '#DA70D6 ',// "National Seed Reserve (NSR)"
    56: '#87CEFA', // "Makhana Board (Beneficiary)"
    57: '#1E90FF', // "Makhana Board (Asset)"
    58: '#1E90FF', // NMEO-OS (CFLD)
    59: '#1E90FF', // NMEO-OS (FLD)
    60: '#1E90FF', // NMEO-OS (SD)
    61: '#DA70D6 ', // Pulse (CFLD) 
    62: '#9ACD32', // Pulse (FLD) 
    63: '#FF6347', //  Pulse (SD) 
    64: '#9370DB',//Cotton (CFLD)
    65: '#4682B4', //Cotton (FLD) 
    66: '#9ACD32', //Cotton (SD) 
    67: '#32CD32', // CDP (CFLD) 
    68: '#DA70D6 ', // CDP (FLD)
    69: '#FF0000',//CDP (SD) 
    70: '#9370DB',// MT-FMTTI Demonstration
    71: '#4682B4', // "MT-CRM Machine Demonstration"
    73: '#DA70D6 ', //"MT-ICAR Machine Demonstration"
    74: '#9ACD32', //"MT-Individual Crm Survey"
    75: '#9370DB', // "MT-Individual SMAM Survey"
};

  return colorMap[code] || '#FF0000';
};

const getBaseImageUrl = () => {
  const baseUrl = process.env.NODE_ENV === "development" 
    ? process.env.REACT_APP_DEV_MODE + "/api/common/GetAttachbyURL?imageURL=" 
    : process.env.REACT_APP_PRO_MODE + "/api/common/GetAttachbyURL?imageURL=";
  
  console.log(' Base Image URL:', baseUrl);
  return baseUrl;
};
// OPTIMIZED: Fetch farmer details with scheme-specific APIs
const fetchFarmerDetails = async (farmerCode, schemeCode, subSchemeCode = null,stateCode = 0) => {
  try {
    const token = localStorage.getItem('token');
    const API_URL = process.env.NODE_ENV === "development" 
      ? process.env.REACT_APP_DEV_MODE 
      : process.env.REACT_APP_PRO_MODE;
      
    // Special handling for AMI (scheme 33)
    if (parseInt(schemeCode) === 33) {
      console.log('🔍 Fetching AMI details for project ID:', farmerCode);
      
      const endpoint = "/api/AMI/AMI/getAMIProjectDetailLists";
      const payload = {
        schemeCode: 33,
        str_FinYear: "",
        stateCode: 0,
        districtCode: 0,
        subDistrictCode: 0,
        blockCode: 0,
        villageCode: 0,
        projectID: farmerCode.toString()
      };
      
      const config = {
        headers: {
          "accept": "/",
          "Content-Type": "application/json",
          ...(token && { "Authorization": `Bearer ${token}` })
        },
        timeout: 30000
      };
      
      const response = await axios.post(`${API_URL}${endpoint}`, payload, config);
      
      console.log('📡 AMI API Response:', response.data);
      
      // Format the response to match expected structure
      if (response.data?.dataContent && Array.isArray(response.data.dataContent)) {
        return {
          dataContent: response.data.dataContent,
          recordCount: response.data.recordCount,
          isAMI: true,
          projectID: farmerCode
        };
      }
      
      return response.data;
    }
    // Special handling for MT CRM (scheme 40)
else if (parseInt(schemeCode) === 40) {
  console.log('🔍 Fetching MT CRM details for CHC ID:', farmerCode);
  
  const endpoint = "/api/MT/Get_GISHCDetailByID";
  const payload = {
    schemeCode: 40,
    chcId: parseInt(farmerCode)
  };
  console.log('📡 MT CRM Request Payload:', payload);
  const config = {
    headers: {
      "accept": "/",
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` })
    },
    timeout: 30000
  };
  
  const response = await axios.post(`${API_URL}${endpoint}`, payload, config);
  
  console.log('📡 MT CRM API Response:', response.data);
  return response.data;
}
    // Special handling for ATMA (scheme 20)
    else if (parseInt(schemeCode) === 20) {
      console.log('🔍 Fetching ATMA details for:', farmerCode);
      
      const endpoint = "/api/Demonstration/getDemonstrationReport";
      const payload = {
        schemeCode: 20,
        cropDemonstrationCode: farmerCode,
        stateCode: null,
        districtCode: null,
        subDistrictCode: null,
        blockCode: null,
        villageCode: null,
        demoType: null,
        finYear: null,
        sortField: null,
        pageNumber: 1,
        pageSize: 100
      };
      
      const config = {
        headers: {
          "accept": "/",
          "Content-Type": "application/json",
          ...(token && { "Authorization": `Bearer ${token}` })
        },
        timeout: 30000
      };
      
      const response = await axios.post(`${API_URL}${endpoint}`, payload, config);
      
      console.log('📡 ATMA API Response:', response.data);
      
      // The response contains multiple beneficiaries under same cropDemonstrationCode
      if (response.data?.dataContent && Array.isArray(response.data.dataContent)) {
        // Return the first beneficiary (or you can return all)
        // The formatFarmerDetails function will handle displaying them
        return {
          dataContent: response.data.dataContent,
          recordCount: response.data.recordCount,
          isATMA: true,
          cropDemonstrationCode: farmerCode
        };
      }
      
      return response.data;
    }
    else if (parseInt(schemeCode) === 7 && subSchemeCode) {
  console.log('🔍 Fetching NBHM details for:', farmerCode);
  
  const subCode = parseInt(subSchemeCode);
  let endpoint, payload;
  
  if (subCode === 4) {
    // Beeflora Plantation - getFarmerDetailsOnly
    endpoint = "/api/Nature/FarmerDetails/getFarmerDetailsList";
    payload = {
       SchemeCode: "7",
          orgTypeCode: 4,
          SurveyUserCode: "0",
          farmerCode: parseInt(farmerCode),
          districtCode: "0",
          subDistrictCode: "0"
         
     
    };
  } else {
    // Honey Processing Unit (1), Honey Testing Unit (2), Honey CHC (3) - getProjectDetailsList
    endpoint = "/api/Nature/FarmerDetails/getProjectDetailsList";
    payload = {
      SchemeCode: "7",
      orgTypeCode: subCode,
     
       farmerCode: farmerCode.toString(),
      
    };
  }
  
  const config = {
    headers: {
      "accept": "/",
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` })
    },
    timeout: 30000
  };
  
  console.log('📡 NBHM Request:', { endpoint, payload });
  
  const response = await axios.post(`${API_URL}${endpoint}`, payload, config);
  
  console.log('📡 NBHM API Response:', response.data);
  return response.data;
}
    
    // Pass subSchemeCode to the helper function
    const { endpoint, payload } = getSchemeEndpointAndPayload(schemeCode, farmerCode, subSchemeCode,stateCode);

    console.log(' Fetching farmer details:', {
      farmerCode,
      schemeCode,
      subSchemeCode,
      fullEndpoint: `${API_URL}${endpoint}`,
      payload
    });

    const config = {
      headers: {
        "accept": "/",
        "Content-Type": "application/json",
        ...(token && { "Authorization": `Bearer ${token}` })
      },
      timeout: 30000
    };

    console.log(' Making API call to:', endpoint);
    console.log(' Payload:', JSON.stringify(payload, null, 2));

    const response = await axios.post(
      `${API_URL}${endpoint}`,
      payload,
      config
    );

    console.log(' Farmer details response status:', response.status);
    console.log(' Response data structure:', {
      hasData: !!response.data,
      isArray: Array.isArray(response.data),
      keys: response.data ? Object.keys(response.data) : 'no data',
      dataContent: response.data?.dataContent
    });

    if (!response.data) {
      throw new Error('Empty response from server');
    }
    
    return response.data;

  } catch (error) {
    console.error('Error fetching farmer details:', error);
    console.error('Error details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      endpoint: error.config?.url,
      method: error.config?.method
    });
    throw error;
  }
};

const escapeHtml = (text) => {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

// Helper function for scheme-specific detail APIs
const getSchemeEndpointAndPayload = (schemeCode, farmerCode, subSchemeCode = null, stateCode = 0) => {
  const scheme = parseInt(schemeCode);
  
  const schemeConfig = {
    1: { // PDMC
      endpoint: "/api/Nature/FarmerDetails/getFarmerDetailsList",
      payload: { schemeCode: scheme, farmerCode }
    },
    2: { // Rainfed Area Development
      endpoint: "/api/Nature/FarmerDetails/getFarmerDetailsList",
      payload: { schemeCode: scheme, farmerCode }
    },
    3: { // Agroforestry
      endpoint: "/api/Nature/FarmerDetails/getProjectDetailsList",
      payload: { schemeCode: 3, projectCode: parseInt(farmerCode) }
    },
    4: { // Seed Hub
      endpoint: "/api/Nature/FarmerDetails/getFarmerDetailsList",
      payload: { schemeCode: scheme, farmerCode }
    },
    5: { // NMEO-OS
      endpoint: "/api/NMEOS/getNMEOSFarmerDetailsAll",
      payload: { schemeCode: 5, lngA_FarmerCode: farmerCode }
    },
    6: { // Natural Farming
      endpoint: "/api/Nature/FarmerDetails/getFarmerDetailsListNew",
      payload: { schemeCode: scheme, farmerCode }
    },
    7: { // NBHM - Handle sub-schemes
  getEndpointAndPayload: () => {
    const subCode = parseInt(subSchemeCode);
    
    console.log('NBHM Sub-scheme processing:', {
      scheme: scheme,
      farmerCode: farmerCode,
      subSchemeCode: subSchemeCode,
      subCode: subCode,
      farmerCodeType: typeof farmerCode
    });
    
    // Sub-scheme 1: NBHM - Honey Processing Unit
    if (subCode === 1) {
      return {
        endpoint: "/api/Nature/FarmerDetails/getProjectDetailsList",
        payload: {
          SchemeCode: "7",
          orgTypeCode: 1,
          SurveyUserCode: "0",
          finYear: "",
          stateCode: stateCode,
          districtCode: "0",
          farmerCode: farmerCode.toString(),
          subDistrictCode: "0",
          villageCode: "0",
          PageNumber: 1,
          dqcStatus: 4
        }
      };
    }
    // Sub-scheme 2: NBHM - Honey Testing Unit
    else if (subCode === 2) {
      return {
        endpoint: "/api/Nature/FarmerDetails/getProjectDetailsList",
        payload: {
          SchemeCode: "7",
          orgTypeCode: 2,
          SurveyUserCode: "0",
          finYear: "",
          stateCode: stateCode,
          farmerCode: farmerCode.toString(),
          districtCode: "0",
          subDistrictCode: "0",
          villageCode: "0",
          PageNumber: 1,
          dqcStatus: 4
        }
      };
    }
    // Sub-scheme 3: NBHM - Honey CHC
    else if (subCode === 3) {
      return {
        endpoint: "/api/Nature/FarmerDetails/getProjectDetailsList",
        payload: {
          SchemeCode: "7",
          orgTypeCode: 3,
          SurveyUserCode: "0",
          finYear: "",
          farmerCode: farmerCode.toString(),
          stateCode: stateCode,
          districtCode: "0",
          subDistrictCode: "0",
          villageCode: "0",
          PageNumber: 1,
          dqcStatus: 4
        }
      };
    }
    // Sub-scheme 4: NBHM - Beeflora Plantation
    else if (subCode === 4) {
      return {
        endpoint: "/api/Nature/FarmerDetails/getFarmerDetailsList",
        payload: {
          SchemeCode: "7",
          orgTypeCode: 4,
          SurveyUserCode: "0",
          farmerCode: parseInt(farmerCode),
         farmerCode: farmerCode,
          stateCode: stateCode,
          districtCode: "0",
          subDistrictCode: "0"
          
        }
      };
    }
    // No fallback - throw error if sub-scheme not specified
    else {
      throw new Error(`NBHM requires sub-scheme specification (1 for Honey Processing Unit, 2 for Honey Testing Unit, 3 for Honey CHC, 4 for Beeflora Plantation)`);
    }
  }
},
    8: { // MIDH
      
      endpoint: "/api/MIDH/MIDHFarmerDetails/GetMidhFarmerDetailsAsync",
      payload: { schemeCode: scheme, stateCode: stateCode,farmerCode }
    },
    9: { // MOVCD
      endpoint: "/api/Nature/FarmerDetails/getFarmerDetailsListNew",
      payload: { schemeCode: scheme, farmerCode }
    },
    10: { // CDB
      endpoint: "/api/CDB/CDB/GetCDBFarmerDetailsAsync",
      payload: { schemeCode: scheme, farmerCode }
    },
    11: { // DASD
      endpoint: "/api/MIDH/MIDHFarmerDetails/GetDasdFarmerDetailsAsync",
      payload: { schemeCode: scheme, farmerCode }
    },
    12: { // Ground Truthing (GT)
      endpoint: "/api/GT/GetSurveyDetails",
      payload: { schemeCode: 12, cropSurveyCode: farmerCode }
    },
    13: { // Crop Health (CH)
      endpoint: "/api/CropInsurance/GetCropHealthFarmersListById",
      payload: { schemeCode: 13, cropSurveyCode: farmerCode }
    },
    14: { // NMEO-OP
      endpoint: "/api/NMEO_OP/getNMEOFarmerDetailsList",
      payload: { schemeCode: scheme, lngA_FarmerCode:farmerCode }
    },
    15: { // RKVY DPR
      endpoint: "/api/RKVY/RKVYProject/RKVYProjectExtList",
      payload: { schemeCode: scheme, farmerCode }
    },
    17: { // DCCD
      endpoint: "/api/MIDH/MIDHFarmerDetails/GetDccdFarmerDetailsAsync",
      payload: { schemeCode: scheme, farmerCode }
    },
    18: { // ACABC - Handle both sub-schemes
      getEndpointAndPayload: () => {
        const subCode = parseInt(subSchemeCode);
        
        console.log('ACABC Sub-scheme processing:', {
          scheme: scheme,
          farmerCode: farmerCode,
          subSchemeCode: subSchemeCode,
          subCode: subCode,
          farmerCodeType: typeof farmerCode
        });
        
        // Sub-scheme 1: Agriventure 
        if (subCode === 1) {
          return {
            endpoint: "/api/reverse/CommonAPI/PostACABC",
            payload: {
              farmerCode: farmerCode.toString(),
              state: "",
              district: "",
              village: ""
            }
          };
        }
        // Sub-scheme 2: Nodal Training Institute
        else if (subCode === 2) {
          return {
            endpoint: "/api/Reverse/CommonAPI/PostInstitutionACABC",
            payload: { schemeCode: scheme, farmerCode }
          };
        }
        // No fallback - throw error if sub-scheme not specified
        else {
          throw new Error(`ACABC requires sub-scheme specification (1 for Agriventure, 2 for Nodal Training Institute)`);
        }
      }
    },
    20: { // ATMA
      endpoint: "/api/Demonstration/getDemonstrationReport",
      payload: {
        schemeCode: 20,
        stateCode: null,
        districtCode: null,
        subDistrictCode: null,
        blockCode: null,
        villageCode: null,
        demoType: parseInt(subSchemeCode),
        finYear: null,
        cropDemonstrationCode: farmerCode
      }
    },
    21: { // National Bamboo Mission
      endpoint: "/api/NBM/NBMFarmerDetails/GetNBMFarmerDetailsAsync",
      payload: { schemeCode: scheme, farmerCode }
    },
  
    22: { // PKVY
      endpoint: "/api/PKVY/getFarmerDetailsByFilter",
      payload: {
        schemeCode: 22,
        farmerCode: farmerCode,
        aadharNo: "",
        casteCatCode: 0,
        mobileNo: "",
        agristackFID: "",
        stateCode: 0,
        districtCode: 0,
        subDistrictCode: 0,
        blockCode: 0,
        villageCode: 0,
        surveyUserCode: 0,
        sortField: ""
      }
    },
     23: { // National Bamboo Mission
      endpoint: "/api/PDMC/PDMCReverseAPIExtn/getPDMCAPIFarmerDetails",
      payload: { farmerCode: farmerCode.toString() }
    },
    24: { // Agri Infra Fund
      endpoint: "/api/AIF/GetAIFDetailsByID", 
      payload: { schemeCode: scheme, loanApplicationNo: farmerCode }
    },
    70: { // MT-FMTTI Demonstration
      endpoint: "/api/ExpensiveMachinery/GetExpBenDataByBenID",
      payload: { beneficiaryId: parseInt(farmerCode) }
    },
    71: { // "MT-CRM Machine Demonstration"
      endpoint: "/api/ExpensiveMachinery/GetExpBenDataByBenID",
      payload: { beneficiaryId: parseInt(farmerCode) }
    },
    73: { // "MT-CRM Machine Demonstration"
      endpoint: "/api/ExpensiveMachinery/GetExpBenDataByBenID",
      payload: { beneficiaryId: parseInt(farmerCode) }
    },
    74: { // "MT-CRM Machine Demonstration"
      endpoint: "/api/ExpensiveMachinery/GetExpBenDataByBenID",
      payload: { beneficiaryId: parseInt(farmerCode) }
    },
    75: { // "MT-CRM Machine Demonstration"
      endpoint: "/api/ExpensiveMachinery/GetExpBenDataByBenID",
      payload: { beneficiaryId: parseInt(farmerCode) }
    },
    27: { // "M&T SMAM"
      endpoint: "/api/MT/Get_GISHCDetailByID",
      payload: { schemeCode: 27, chcId: parseInt(farmerCode) }
    },
    29: { // Seed Hub
      endpoint: "/api/SeedHub/FetchSeedHubDetailIdWise",
      payload: { id: farmerCode.toString() }
    },
    33: { // AMI - Project Details
      endpoint: "/api/AMI/AMI/getAMIProjectDetailLists",
      payload: { 
        schemeCode: 33,
        str_FinYear: "",
        stateCode: 0,
        districtCode: 0,
        subDistrictCode: 0,
        blockCode: 0,
        villageCode: 0,
        projectID: farmerCode.toString()
      }
    },
    40: { // "M&T CRM"
      endpoint: "/api/MT/Get_GISHCDetailByID",
      payload: { schemeCode: 40, chcId: parseInt(farmerCode) }
    }
  };
  
  const config = schemeConfig[scheme];
  
  // Handle ACABC special case with dynamic endpoint based on sub-scheme
  if (scheme === 18 && config && config.getEndpointAndPayload && subSchemeCode) {
    console.log('Using ACABC sub-scheme specific endpoint');
    return config.getEndpointAndPayload();
  }
  
  // For other schemes or if config is not found
  return config;
};
const formatFarmerDetails = (details, schemeCode, selectedScheme = null) => {
  let dataContent = details.dataContent || details.recordCount || details;
  
  if (!dataContent || (Array.isArray(dataContent) && dataContent.length === 0)) {
    // Create a unique ID for this popup
    const popupId = `no-data-popup-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Return HTML with auto-close functionality
    return `
      <div id="${popupId}" class="farmer-details-popup" style="text-align: center; padding: 30px;">
        <h6 style="margin-bottom: 15px; color: #dc3545; font-size: 16px;">No Data Available</h6>
        <p style="color: #666; margin-bottom: 10px;">No detailed information found for this farmer</p>
        <small style="color: #999;">Scheme: ${getSchemeNameByCode(schemeCode, selectedScheme?.subSchemeCode)}</small>
      </div>
      <script>
        (function() {
          setTimeout(function() {
            var popup = document.getElementById('${popupId}');
            if (popup) {
              // Find the closest leaflet popup and close it
              var leafletPopup = popup.closest('.leaflet-popup');
              if (leafletPopup) {
                var closeButton = leafletPopup.querySelector('.leaflet-popup-close-button');
                if (closeButton) {
                  closeButton.click();
                }
              }
            }
          }, 3000);
        })();
      </script>
    `;
  }

  const farmerData = Array.isArray(dataContent) ? dataContent[0] : dataContent;

  if (!farmerData) {
    return '<p style="text-align: center; padding: 30px; color: #666;">No farmer data available</p>';
  }

  const baseImageUrl = getBaseImageUrl();
  let imagePath = '';
  let isFullUrl = false;

  const scheme = parseInt(schemeCode);

  // Image extraction logic
  if (scheme === 5) {
    // NMEO-OS - Use farmerAadhaarImageList
    if (farmerData.farmerAadhaarImageList && farmerData.farmerAadhaarImageList.length > 0) {
      imagePath = farmerData.farmerAadhaarImageList[0].attPath || '';
    }
  }

   else if (scheme === 12) {
    // GT (Ground Truthing)
    if (farmerData.surveyImageList && Array.isArray(farmerData.surveyImageList) && farmerData.surveyImageList.length > 0) {
      const firstImage = farmerData.surveyImageList[0];
      if (firstImage && firstImage.attPath) {
        imagePath = firstImage.attPath;
        imagePath = `${baseImageUrl}${encodeURIComponent(imagePath)}`;
      }
    }
  } 
  else if (scheme === 24) {
  // AIF - Handle multiple images from aifImagesList
  if (farmerData.aifImagesList && Array.isArray(farmerData.aifImagesList) && farmerData.aifImagesList.length > 0) {
    // Store all images for later use with navigation
    window._aifImagesCache = window._aifImagesCache || {};
    const popupId = `aif-images-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    window._aifImagesCache[popupId] = farmerData.aifImagesList;
    
   
   imagePath = farmerData.aifImagesList[0].aifImage;
    isFullUrl = true; // Important: These are already full URLs
    window._currentAIFPopupId = popupId; // Store for the image tab to use
  } else if (farmerData.aifImage) {
    imagePath = farmerData.aifImage;
    isFullUrl = true;
  }
}
  else if (scheme === 24) {
  // AIF - Handle multiple images from aifImagesList
  if (farmerData.aifImagesList && Array.isArray(farmerData.aifImagesList) && farmerData.aifImagesList.length > 0) {
    // Store all images for later use in the image tab
    window._aifImagesCache = window._aifImagesCache || {};
    const popupId = `aif-images-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    window._aifImagesCache[popupId] = farmerData.aifImagesList;
    
    // For backward compatibility, set the first image as the main image
    imagePath = farmerData.aifImagesList[0].aifImage;
    isFullUrl = true; // These are already full URLs
  } else if (farmerData.aifImage) {
    imagePath = farmerData.aifImage;
    isFullUrl = true;
  }
}

  else if (scheme === 13) {
    // CROP HEALTH - Updated to match your JSON structure
    if (farmerData.surveyImageList && Array.isArray(farmerData.surveyImageList) && farmerData.surveyImageList.length > 0) {
      const firstImage = farmerData.surveyImageList[0];
      if (firstImage && firstImage.attPath) {
        imagePath = firstImage.attPath;
        isFullUrl = true; // The path already includes the full URL
      }
    } else if (farmerData.farmerCropSurveyImageList && farmerData.farmerCropSurveyImageList.length > 0) {
      imagePath = farmerData.farmerCropSurveyImageList[0].attPath || '';
    } else if (farmerData.attPath) {
      imagePath = farmerData.attPath;
    }
  } else if (scheme === 13) {
    // CROP HEALTH
    if (farmerData.surveyImageList && Array.isArray(farmerData.surveyImageList) && farmerData.surveyImageList.length > 0) {
      const firstImage = farmerData.surveyImageList[0];
      if (firstImage && firstImage.attPath) {
        imagePath = firstImage.attPath;
      }
    } else if (farmerData.farmerCropSurveyImageList && farmerData.farmerCropSurveyImageList.length > 0) {
      imagePath = farmerData.farmerCropSurveyImageList[0].attPath || '';
    } else if (farmerData.attPath) {
      imagePath = farmerData.attPath;
    }
  } else if (scheme === 15) {
    // RKVY DPR
    if (farmerData.projectRKVYImageLists && Array.isArray(farmerData.projectRKVYImageLists) && farmerData.projectRKVYImageLists.length > 0) {
      const firstImage = farmerData.projectRKVYImageLists[0];
      if (firstImage && firstImage.attPath) {
        imagePath = firstImage.attPath;
        isFullUrl = true;
      }
    }
    
    if (!imagePath) {
      if (farmerData.farmerCropSurveyImageList && farmerData.farmerCropSurveyImageList.length > 0) {
        imagePath = farmerData.farmerCropSurveyImageList[0].attPath || '';
      } else if (farmerData.attPath) {
        imagePath = farmerData.attPath;
      }
    }
  }
   else if (scheme === 22) {
    // PKVY
    console.log('🔍 PKVY - Processing farmer data:', farmerData);
    
    if (farmerData.images && Array.isArray(farmerData.images) && farmerData.images.length > 0) {
      const firstImage = farmerData.images[0];
      console.log('🔍 PKVY - First image object:', firstImage);
      
      if (firstImage && firstImage.imagepath) {
        imagePath = firstImage.imagepath;
        console.log('🔍 PKVY - Found imagepath:', imagePath);
      } else if (firstImage && firstImage.imagePath) {
        imagePath = firstImage.imagePath;
        console.log('🔍 PKVY - Found imagePath:', imagePath);
      } else if (firstImage && firstImage.path) {
        imagePath = firstImage.path;
        console.log('🔍 PKVY - Found path:', imagePath);
      }
    }
    
    if (!imagePath) {
      if (farmerData.farmerCropSurveyImageList && farmerData.farmerCropSurveyImageList.length > 0) {
        imagePath = farmerData.farmerCropSurveyImageList[0].attPath || '';
        console.log('🔍 PKVY - Using farmerCropSurveyImageList:', imagePath);
      } else if (farmerData.attPath) {
        imagePath = farmerData.attPath;
        console.log('🔍 PKVY - Using attPath:', imagePath);
      }
    }
    
    console.log('🔍 PKVY - Final image path extracted:', imagePath);
  }else if (scheme === 27 || scheme === 40){
  // MT SMAM - Handle image path correctly
  if (farmerData.imagePath) {
    // Check if it's already a full URL
    if (farmerData.imagePath.startsWith('http')) {
      imagePath = farmerData.imagePath;
      isFullUrl = true;
    } else {
      // If it's a relative path, construct the full URL
      imagePath = farmerData.imagePath;
      isFullUrl = false; // Will use baseImageUrl
    }
    console.log('🔍 MT SMAM - Image path:', imagePath);
  } else if (farmerData.attPath) {
    imagePath = farmerData.attPath;
    console.log('🔍 MT SMAM - Using attPath:', imagePath);
  } else if (farmerData.farmerCropSurveyImageList && farmerData.farmerCropSurveyImageList.length > 0) {
    imagePath = farmerData.farmerCropSurveyImageList[0].attPath || '';
    console.log('🔍 MT SMAM - Using farmerCropSurveyImageList:', imagePath);
  }
}
else if ([70, 71, 73, 74, 75].includes(scheme)) {
  // M&T schemes - imagePath1 has comma-separated full URLs
  if (farmerData.imagePath1 && farmerData.imagePath1.trim() !== '') {
    imagePath = farmerData.imagePath1.split(',')[0].trim();
    isFullUrl = true;
  }
}
else {
  // ALL OTHER SCHEMES
  if (farmerData.farmerCropSurveyImageList && farmerData.farmerCropSurveyImageList.length > 0) {
    imagePath = farmerData.farmerCropSurveyImageList[0].attPath || '';
  } else if (farmerData.attPath) {
    imagePath = farmerData.attPath;
  }

if (imagePath && imagePath.startsWith('http')) isFullUrl = true;
}

  // Define desired fields based on scheme
  let desiredFields = [];
   if (scheme === 33) {
    // AMI specific fields - MOVED TO THE TOP LEVEL
    desiredFields = [
      { key: 'projectName', label: 'Project Name' },
      { key: 'beneficiaryType', label: 'Beneficiary Type' },
      { key: 'typeofProject', label: 'Project Type' },
      { key: 'sanctionedAmount', label: 'Sanctioned Amount (₹)' },
      { key: 'sanctionedDate', label: 'Sanctioned Date' },
      { key: 'address', label: 'Address' },
      { key: 'establishedLandUnits', label: 'Land Ownership' },
      { key: 'stateName', label: 'State' },
      { key: 'districtName', label: 'District' },
      { key: 'pinCode', label: 'Pin Code' },
      { key: 'dateofInspection', label: 'Date of Inspection' },
      { key: 'createdOn', label: 'Created On' },
      { key: 'latitude', label: 'Latitude' },
      { key: 'longitude', label: 'Longitude' }
    ];
  }
  else if (scheme === 7) {
  // NBHM specific fields based on sub-scheme
  const subCode = parseInt(selectedScheme?.subSchemeCode);
  
  if (subCode === 4) {
    // Beeflora Plantation - farmer details
    desiredFields = [
      { key: 'farmerName', label: 'Farmer Name' },
      { key: 'fatherName', label: 'Father Name' },
      { key: 'aadharNo', label: 'Aadhar No' },
      { key: 'mobileNo', label: 'Mobile No' },
      { key: 'gender', label: 'Gender' },
      { key: 'stateName', label: 'State' },
      { key: 'districtName', label: 'District' },
      { key: 'subDistrictName', label: 'Sub District' },
      { key: 'blockName', label: 'Block' },
      { key: 'villageName', label: 'Village' },
      { key: 'surveyNo', label: 'Survey No' },
      { key: 'landArea', label: 'Land Area (ha)' },
      { key: 'plantationArea', label: 'Plantation Area (ha)' },
      { key: 'noOfPlants', label: 'No of Plants' },
      { key: 'plantType', label: 'Plant Type' },
      { key: 'finYear', label: 'Financial Year' },
      { key: 'createdOn', label: 'Created On' },
      { key: 'userName', label: 'Surveyor Name' }
    ];
  } else {
    // Honey Processing Unit / Testing Unit / CHC - project details
    desiredFields = [
      { key: 'projectName', label: 'Project Name' },
      { key: 'orgTypeName', label: 'Organization Type' },
      { key: 'beneficiaryName', label: 'Beneficiary Name' },
      { key: 'aadharNo', label: 'Aadhar No' },
      { key: 'mobileNo', label: 'Mobile No' },
      { key: 'stateName', label: 'State' },
      { key: 'districtName', label: 'District' },
      { key: 'subDistrictName', label: 'Sub District' },
      { key: 'blockName', label: 'Block' },
      { key: 'villageName', label: 'Village' },
      { key: 'surveyNo', label: 'Survey No' },
      { key: 'landArea', label: 'Land Area (ha)' },
      { key: 'projectCost', label: 'Project Cost (₹)' },
      { key: 'sanctionedAmount', label: 'Sanctioned Amount (₹)' },
      { key: 'finYear', label: 'Financial Year' },
      { key: 'createdOn', label: 'Created On' },
      { key: 'userName', label: 'Surveyor Name' }
    ];
  }
}
else if (scheme === 12) {
    // GT specific fields based on your response
    desiredFields = [
      { key: 'surveyCode', label: 'Survey Code' },
      { key: 'surveyorName', label: 'Surveyor Name' },
      { key: 'userMobile', label: 'Mobile No' },
      { key: 'stateName', label: 'State Name' },
      { key: 'districtName', label: 'District Name' },
      { key: 'subDistrictName', label: 'Sub District Name' },
      { key: 'villageName', label: 'Village Name' },
      { key: 'seasonName', label: 'Season Name' },
      { key: 'cropCategoryName', label: 'Crop Category' },
      { key: 'cropName', label: 'Crop Name' },
      { key: 'cropConditionName', label: 'Crop Condition' },
      { key: 'cropStageName', label: 'Crop Stage' },
      { key: 'cropingTypeName', label: 'Cropping Type' },
      { key: 'plotArea', label: 'Plot Area (ha)' },
      { key: 'polygonArea', label: 'Polygon Area (sq.m)' },
      { key: 'showingDate', label: 'Showing Date' },
      { key: 'createdOnByUC', label: 'Created On' },
      { key: 'surveyorLat', label: 'Surveyor Latitude' },
      { key: 'surveyorLong', label: 'Surveyor Longitude' }
    ];
  }
  else if ([70, 71, 73, 74, 75].includes(scheme)) {
  // M&T Demonstration schemes (FMTTI, CRM Machine Demo, ICAR, Individual Surveys)
  desiredFields = [
    { key: 'stateName',            label: 'State' },
    { key: 'districtName',         label: 'District' },
    { key: 'subdistrictName',      label: 'Sub District' },
    { key: 'villageName',          label: 'Village' },
    { key: 'farmerName',           label: 'Farmer Name' },
    { key: 'fatherHusbandName',    label: 'Father / Husband Name' },
    { key: 'aadhaarNo',            label: 'Aadhaar No' },
    { key: 'genderName',           label: 'Gender' },
    { key: 'casteCategoryName',    label: 'Caste Category' },
    { key: 'mobileNumber',         label: 'Mobile Number' },
    { key: 'financialYear',        label: 'Financial Year' },
    { key: 'componentID',          label: 'Component ID' },
    { key: 'componentName',        label: 'Component Name' },
    { key: 'categoryName',         label: 'Category Name' },
    { key: 'implementName',        label: 'Implement Name' }
  ];
}
 // In formatFarmerDetails function, replace the ATMA section:

else if (parseInt(schemeCode) === 20 && details.isATMA) {
  const beneficiaries = details.dataContent;
  
  if (!beneficiaries || beneficiaries.length === 0) {
    return `<div style="padding: 20px; text-align: center;">No beneficiary data available</div>`;
  }
  
  const popupId = `atma-popup-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const storageKey = `atma_data_${popupId}`;
  const modalId = `beneficiary-modal-${popupId}`;
  const modalContentId = `beneficiary-modal-content-${popupId}`;
  
  // Store beneficiary data globally
  window[storageKey] = beneficiaries;
  
  // Register the click handler function globally only once
  if (!window.viewATMAPopupDetails) {
    window.viewATMAPopupDetails = function(beneficiaryId, index, storageKey, modalId, modalContentId, popupId) {
      console.log('viewATMAPopupDetails called:', { beneficiaryId, index, storageKey });
      
      try {
        // Get beneficiary data from stored global variable
        const beneficiaries = window[storageKey];
        if (!beneficiaries) {
          console.error('Beneficiaries not found for key:', storageKey);
          alert('Beneficiary data not found');
          return;
        }
        
        const beneficiary = beneficiaries[index];
        if (!beneficiary) {
          console.error('Beneficiary not found at index:', index);
          alert('Beneficiary data not found at index ' + index);
          return;
        }
        
        console.log('Beneficiary found:', beneficiary.farmerName);
        
        // Get modal elements
        const modal = document.getElementById(modalId);
        const modalContent = document.getElementById(modalContentId);
        
        if (!modal || !modalContent) {
          console.error('Modal elements not found:', { modalId, modalContentId });
          return;
        }
        
        // Build detailed view HTML
        let detailsHtml = `
          <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 12px 16px; display: flex; justify-content: space-between; align-items: center;">
            <h6 style="margin: 0; color: white; font-size: 14px; font-weight: 600;">Beneficiary Details</h6>
            <button onclick="document.getElementById('${modalId}').style.display='none'" style="background: transparent; border: none; color: white; font-size: 20px; cursor: pointer;">×</button>
          </div>
          <div style="padding: 20px; max-height: 70vh; overflow-y: auto;">
            <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
        `;
        
        // Define fields to display for ATMA beneficiary
        const atmaDetailFields = [
          { key: 'farmerName', label: 'Farmer Name' },
          { key: 'fatherSpousesName', label: 'Father/Spouse Name' },
          { key: 'mobileNo', label: 'Mobile No' },
          { key: 'gender', label: 'Gender' },
          { key: 'casterCategory', label: 'Caste Category' },
          { key: 'beneficiaryCode', label: 'Beneficiary Code' },
          { key: 'demoType', label: 'Demo Type' },
          { key: 'financialYear', label: 'Financial Year' },
          { key: 'season', label: 'Season' },
          { key: 'cropGroup', label: 'Crop Group' },
          { key: 'crop', label: 'Crop' },
          { key: 'cropVariety', label: 'Crop Variety' },
          { key: 'demoAreaInHa', label: 'Demo Area (Ha)' },
          { key: 'stateName', label: 'State' },
          { key: 'districtName', label: 'District' },
          { key: 'blockName', label: 'Block' },
          { key: 'villageName', label: 'Village' },
          { key: 'surveyorName', label: 'Surveyor Name' }
        ];
        
        atmaDetailFields.forEach(field => {
          let value = beneficiary[field.key];
          if (value && value !== null && value !== '' && value !== 'null') {
            detailsHtml += `
              <tr style="border-bottom: 1px solid #e9ecef;">
                <td style="padding: 8px 12px; width: 35%; background: #f8f9fa; font-weight: 600;">${field.label}</td>
                <td style="padding: 8px 12px; width: 65%;">${escapeHtml(String(value))}</td>
               </tr>
            `;
          }
        });
        
        // Add polygon drawn status
        let hasPolygon = false;
        if (beneficiary.polygonShape && beneficiary.polygonShape !== "null" && beneficiary.polygonShape !== "N/A" && beneficiary.polygonShape !== "") {
          hasPolygon = true;
        }
        
        detailsHtml += `
              <tr style="border-bottom: 1px solid #e9ecef;">
                <td style="padding: 8px 12px; background: #f8f9fa; font-weight: 600;">Polygon Drawn</td>
                <td style="padding: 8px 12px;">
                  ${hasPolygon ? 
                    '<span style="display: inline-block; padding: 4px 8px; border-radius: 4px; background: #d4edda; color: #155724;">✓ Yes</span>' : 
                    '<span style="display: inline-block; padding: 4px 8px; border-radius: 4px; background: #f8d7da; color: #721c24;">✗ No</span>'
                  }
                </td>
               </tr>
        `;
        
        detailsHtml += `
            </table>
          </div>
          <div style="padding: 12px 16px; border-top: 1px solid #e9ecef; text-align: right;">
            <button onclick="document.getElementById('${modalId}').style.display='none'" 
                    style="background: #6c757d; color: white; border: none; border-radius: 4px; padding: 6px 12px; cursor: pointer;">
              Close
            </button>
          </div>
        `;
        
        modalContent.innerHTML = detailsHtml;
        modal.style.display = 'block';
        
        // Close modal when clicking outside
        modal.onclick = function(e) {
          if (e.target === modal) {
            modal.style.display = 'none';
          }
        };
        
      } catch (error) {
        console.error('Error showing beneficiary details:', error);
        alert('Error loading beneficiary details: ' + error.message);
      }
    };
  }
  
  // Create HTML with table view
  let html = `
    ${draggablePopupStyles}
    <div id="${popupId}" class="farmer-details-popup" style="max-width: 900px; margin: 0 auto; background: white; border-radius: 8px; overflow: hidden; box-shadow: 0 10px 40px rgba(0,0,0,0.15);">
      
      <!-- Header -->
      <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 12px 16px; color: white;">
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <div>
            <strong style="font-size: 14px;">ATMA Demonstration Details</strong>
            <div style="font-size: 11px; opacity: 0.9;">Code: ${details.cropDemonstrationCode}</div>
          </div>
          <div style="background: rgba(255,255,255,0.2); padding: 4px 8px; border-radius: 12px; font-size: 12px;">
            ${beneficiaries.length} Beneficiaries
          </div>
        </div>
      </div>
      
      <!-- Table View -->
      <div style="padding: 16px; max-height: 500px; overflow-y: auto;">
        <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
          <thead>
            <tr style="background: #f8f9fa; border-bottom: 2px solid #dee2e6;">
              <th style="padding: 12px; text-align: left; font-weight: 600; color: #495057;">S.No.</th>
              <th style="padding: 12px; text-align: left; font-weight: 600; color: #495057;">Farmer Name</th>
              <th style="padding: 12px; text-align: left; font-weight: 600; color: #495057;">Demo Area (Ha)</th>
              <th style="padding: 12px; text-align: center; font-weight: 600; color: #495057;">Polygon Drawn</th>
              <th style="padding: 12px; text-align: center; font-weight: 600; color: #495057;">Action</th>
             </tr>
          </thead>
          <tbody>
  `;
  
  beneficiaries.forEach((beneficiary, index) => {
    const beneficiaryId = `beneficiary-${popupId}-${index}`;
    const farmerName = beneficiary.farmerName || `Beneficiary ${index + 1}`;
    const demoArea = beneficiary.demoAreaInHa || beneficiary.areaInHa || 'N/A';
    
    // Check if polygon is drawn
    let hasPolygon = false;
    let polygonShape = beneficiary.polygonShape;
    
    if (polygonShape && polygonShape !== "null" && polygonShape !== "N/A" && polygonShape !== "" && polygonShape !== "[]") {
      try {
        let cleanedShape = polygonShape.toString().trim();
        cleanedShape = cleanedShape.replace(/^["']|["']$/g, '').replace(/\\"/g, '"');
        
        if (cleanedShape && cleanedShape !== '[]' && cleanedShape !== '{}') {
          let parsed;
          try {
            parsed = JSON.parse(cleanedShape);
          } catch (jsonError) {
            const coordMatches = cleanedShape.match(/[-+]?[0-9]*\.?[0-9]+/g);
            if (coordMatches && coordMatches.length >= 6) {
              parsed = coordMatches;
            }
          }
          
          if (parsed && (Array.isArray(parsed) && parsed.length > 0)) {
            hasPolygon = true;
          } else if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
            hasPolygon = true;
          }
        }
      } catch (e) {
        console.error('Error parsing polygon for beneficiary:', e);
      }
    }
    
    html += `
      <tr style="border-bottom: 1px solid #e9ecef; ${index % 2 === 0 ? 'background-color: #fff;' : 'background-color: #f8f9fa;'}">
        <td style="padding: 10px 12px; vertical-align: middle; color: #6c757d;">${index + 1}</td>
        <td style="padding: 10px 12px; vertical-align: middle; font-weight: 500; color: #212529;">${escapeHtml(farmerName)}</td>
        <td style="padding: 10px 12px; vertical-align: middle; color: #495057;">${demoArea}</td>
        <td style="padding: 10px 12px; vertical-align: middle; text-align: center;">
          ${hasPolygon ? 
            '<span style="display: inline-block; padding: 4px 8px; border-radius: 4px; font-size: 11px; background: #d4edda; color: #155724;">✓ Yes</span>' : 
            '<span style="display: inline-block; padding: 4px 8px; border-radius: 4px; font-size: 11px; background: #f8d7da; color: #721c24;">✗ No</span>'
          }
        </td>
        <td style="padding: 10px 12px; vertical-align: middle; text-align: center;">
          <button 
            class="view-details-btn"
            onclick="window.viewATMAPopupDetails('${beneficiaryId}', ${index}, '${storageKey}', '${modalId}', '${modalContentId}', '${popupId}')"
            style="
              background: #007bff;
              color: white;
              border: none;
              border-radius: 4px;
              padding: 6px 12px;
              font-size: 11px;
              cursor: pointer;
              transition: all 0.2s;
            "
            onmouseover="this.style.backgroundColor='#0056b3'"
            onmouseout="this.style.backgroundColor='#007bff'"
          >
            View Details
          </button>
        </td>
       </tr>
    `;
  });
  
  html += `
          </tbody>
        </table>
      </div>
      
      <!-- Modal for individual beneficiary details -->
      <div id="${modalId}" style="display: none; position: fixed; z-index: 10000; left: 0; top: 0; width: 100%; height: 100%; background-color: rgba(0,0,0,0.5);">
        <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 90%; max-width: 600px; max-height: 90vh; overflow-y: auto; background: white; border-radius: 8px; box-shadow: 0 10px 40px rgba(0,0,0,0.3);">
          <div id="${modalContentId}">
            <!-- Content will be loaded here -->
          </div>
        </div>
      </div>
      
    </div>
    
    <script>
      // Ensure the popup is draggable
      setTimeout(function() {
        var popup = document.getElementById('${popupId}');
        if (popup && window.makePopupDraggable) {
          if (popup._dragCleanup) popup._dragCleanup();
          window.makePopupDraggable(popup);
        }
      }, 200);
    </script>
  `;
  
  return html;
}
  else if (scheme === 22) {
    // PKVY specific fields based on your data structure
    desiredFields = [
      { key: 'farmerName', label: 'Farmer Name' },
      { key: 'aadharNo', label: 'Aadhar No' },
      { key: 'gender', label: 'Gender' },
      { key: 'mobileNo', label: 'Mobile No' },
      { key: 'stateName', label: 'State' },
      { key: 'districtName', label: 'District' },
      { key: 'subDistrictName', label: 'Sub District' },
      { key: 'blockName', label: 'Block' },
      { key: 'villageName', label: 'Village' },
      { key: 'cropName', label: 'Crop Name' },
      { key: 'areaCovered_Ha', label: 'Area Covered (Ha)' },
      { key: 'totalAreaInHA', label: 'Total Area (Ha)' },
      { key: 'surveyNo', label: 'Survey No' },
      { key: 'finYear', label: 'Financial Year' },
      { key: 'createdOn', label: 'Created On' }
    ];
  }
  else if (scheme === 13) {
    // CROP HEALTH specific fields based on your JSON
    desiredFields = [
      { key: 'seasonName', label: 'Season Name' },
      { key: 'cropCategoryName', label: 'Crop Category' },
      { key: 'cropName', label: 'Crop Name' },
      { key: 'cropConditionName', label: 'Crop Condition' },
      { key: 'cropStageName', label: 'Crop Stage' },
      { key: 'showingDate', label: 'Showing Date' },
      { key: 'harvestingDate', label: 'Harvesting Date' },
      { key: 'plotArea', label: 'Plot Area (ha)' },
      { key: 'cropingTypeName', label: 'Cropping Type' },
      { key: 'stateName', label: 'State Name' },
      { key: 'districtName', label: 'District Name' },
      { key: 'subDistrictName', label: 'Sub District Name' },
      { key: 'villageName', label: 'Village Name' },
      { key: 'polygonArea', label: 'Polygon Area (sq.m)' },
      { key: 'soilCondition', label: 'Soil Condition' },
      { key: 'surveyorName', label: 'Surveyor Name' },
      { key: 'userMobile', label: 'Mobile No' },
      { key: 'company_name', label: 'Company Name' },
      { key: 'createdOnByUC', label: 'Created On' },
      { key: 'userName', label: 'User Name' }
    ];}
    else if (scheme === 24) {
    // AIF specific fields
    desiredFields = [
      { key: 'beneficiaryType', label: 'Beneficiary Type' },
      { key: 'stateName', label: 'State' },
      { key: 'districtName', label: 'District' },
      { key: 'villageName', label: 'Village' },
      { key: 'projectName', label: 'Project Name' },
      { key: 'projectType', label: 'Project Type' },
      { key: 'projectCost', label: 'Project Cost (₹)' },
      { key: 'approvedLoanAmountAIF', label: 'Approved Loan Amount (₹)' },
      { key: 'approvedLoanTermYear', label: 'Loan Term (Years)' },
      { key: 'loanApplicationStatus', label: 'Application Status' },
      { key: 'loanAppNo', label: 'Loan Application No.' },
      { key: 'project_Geo_Update_Date', label: 'Geo Update Date' }
    ];
  }
  else if (scheme === 15) {
    // RKVY DPR specific fields based on your data structure
    desiredFields = [
      { key: 'projectId', label: 'Project ID' },
      { key: 'projectName', label: 'Project Name' },
      { key: 'sectorName', label: 'Sector' },
      { key: 'subSectorName', label: 'Sub Sector' },
      { key: 'stateName', label: 'State' },
      { key: 'districtName', label: 'District' },
      { key: 'subDistrictName', label: 'Sub District' },
      { key: 'villageName', label: 'Village' },
      { key: 'surveyNumber', label: 'Survey Number' },
      { key: 'projectApprovalDate', label: 'Project Approval Date' },
      { key: 'projectStartingDate', label: 'Project Start Date' },
      { key: 'expenditureReportDate', label: 'Expenditure Report Date' },
      { key: 'surveyorCode', label: 'Surveyor Code' },
      { key: 'userName', label: 'Surveyor Name' },
      { key: 'createdOn', label: 'Created On' }
    ];
  } 
   else {
    // Default fields for other schemes
    desiredFields = [
      { key: 'farmerName', label: 'Farmer Name' },
      { key: 'aadharNo', label: 'Aadhar No' },
      { key: 'stateName', label: 'State Name' },
      { key: 'districtName', label: 'District Name' },
      { key: 'subDistrictName', label: 'Sub District Name' },
      { key: 'blockName', label: 'Block Name' },
       { key: 'componentName', label: 'Component Name' },
      { key: 'villageName', label: 'Village Name' },
      { key: 'mobileNo', label: 'Mobile No' },
      { key: 'farmerType', label: 'Farmer Type' },
      { key: 'finYear', label: 'Financial Year' },  
      { key: 'seasonName', label: 'Season Name' },   
      { key: 'activityType', label: 'Activity Type' },
      { key: 'activitySubType', label: 'Sub Activity Type' },
      { key: 'landArea', label: 'Land Area' },
      { key: 'surveyNo', label: 'Survey No' },
      { key: 'cropGroupName', label: 'Crop Group Name' }, 
      { key: 'cropName', label: 'Crop Name' },             
      { key: 'createdOn', label: 'Created On' },
      { key: 'userName', label: 'Surveyor Name' },
      { key: 'surveyUserCode', label: 'surveyUserCode' },
      { key: 'userName', label: 'Surveyor Name' },
       { key: 'cropCategoryName', label: 'Crop Category Name' },
       { key: 'surveyorName', label: 'Surveyor Name' },
       { key: 'gender', label: 'Gender' }

    ];
  }

if (scheme === 27 || scheme === 40) {
  // MT SMAM specific fields based on what Get_GISHCDetailByID returns
  desiredFields = [
    { key: 'chcId', label: 'CHC ID' },
    { key: 'chcName', label: 'Farmer Name' },
    { key: 'aadharNo', label: 'Aadhar No' },
    { key: 'mobileNo', label: 'Mobile No' },
    { key: 'stateName', label: 'State' },
    { key: 'districtName', label: 'District' },
    { key: 'blockName', label: 'Block' },
    { key: 'villageName', label: 'Village' },
    { key: 'surveyNo', label: 'Survey No' },
    { key: 'noOfImplements', label: 'No of Implements', clickable: true },
    { key: 'landArea', label: 'Land Area (ha)' },
    { key: 'cropName', label: 'Crop Name' },
    { key: 'variety', label: 'Variety' },
    { key: 'sowingDate', label: 'Sowing Date' },
    { key: 'expectedHarvestDate', label: 'Expected Harvest Date' },
    { key: 'schemeName', label: 'Scheme Name' },
    { key: 'financialYear', label: 'Financial Year' },
    { key: 'createdOn', label: 'Created On' }
  ];
}
  
// In the formatFarmerDetails function, modify the addTableRow function
const addTableRow = (label, value, fieldKey, farmerData) => {
  if (value !== undefined && value !== null && value !== 'N/A' && value !== 'null' && value !== '') {
    let displayValue = value;
    let isClickable = false;
    
    // Check if this field should be clickable
    if ((scheme === 27 || scheme === 40) && fieldKey === 'noOfImplements') {
  isClickable = true;
  const chcId = farmerData.chcId;
  
  const modalId = `implement-modal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const contentId = `implement-content-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  // Add the modal HTML to the popup
  setTimeout(() => {
    const popup = document.getElementById(popupId);
    if (popup) {
      // Add modal HTML to the popup if not already present
      if (!document.getElementById(modalId)) {
        const modalDiv = document.createElement('div');
        modalDiv.id = modalId;
        modalDiv.style.display = 'none';
        modalDiv.style.position = 'fixed';
        modalDiv.style.zIndex = '10000';
        modalDiv.style.left = '0';
        modalDiv.style.top = '0';
        modalDiv.style.width = '100%';
        modalDiv.style.height = '100%';
        modalDiv.style.backgroundColor = 'rgba(0,0,0,0.5)';
        modalDiv.innerHTML = `<div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); min-width: 300px; max-width: 700px; max-height: 90vh; overflow-y: auto;" id="${contentId}"></div>`;
        document.body.appendChild(modalDiv);
      }
    }
  }, 100);
  
  displayValue = `
    <span style="cursor: pointer; color: #007bff; text-decoration: underline; display: inline-flex; align-items: center; gap: 4px;" 
          onclick="window.handleImplementClick('${chcId}', '${modalId}', '${contentId}')">
      <i class="bi bi-tools" style="font-size: 14px;"></i>
      ${value} (Click to view)
    </span>
  `;
} else {
      // Handle other field types
      if (typeof value === 'boolean') {
        displayValue = value ? 'Yes' : 'No';
      } else if (typeof value === 'object') {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            if (typeof value[0] === 'object') {
              displayValue = `[${value.length} items]`;
            } else {
              displayValue = value.join(', ');
            }
          } else {
            displayValue = '';
          }
        } else {
          displayValue = JSON.stringify(value);
        }
      }
    }
    
    if (displayValue) {
      return `
        <tr>
          <td style="padding: 10px 15px; border: 1px solid #e9ecef; width: 40%; background-color: #f8f9fa; font-weight: 600; text-align: left; color: #495057;">${label}</td>
          <td style="padding: 10px 15px; border: 1px solid #e9ecef; width: 60%; text-align: left; word-break: break-word; color: #212529;">${displayValue}</td>
        </tr>
      `;
    }
  }
  return '';
};

  const modalId = `image-modal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const tab1Id = `tab-${Date.now()}-1`;
  const tab2Id = `tab-${Date.now()}-2`;
  const popupId = `popup-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Start building HTML
  let html = `
    ${draggablePopupStyles}
    <div id="${popupId}" class="farmer-details-popup" style="max-width: 600px; margin: 0 auto; background: white; border-radius: 8px; overflow: hidden; box-shadow: 0 10px 40px rgba(0,0,0,0.15);">
      
      <!-- Simple header -->
      <div style="
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        padding: 12px 16px;
        border-bottom: 1px solid rgba(255,255,255,0.1);
        display: flex;
        align-items: center;
        justify-content: space-between;
        color: white;
        font-weight: 500;
      ">
        <div style="display: flex; align-items: center; gap: 8px;">
          <span style="font-weight: 600; font-size: 14px;">
            ${getSchemeNameByCode(schemeCode, selectedScheme?.subSchemeCode)} - ${scheme === 12 ? 'Survey Details' : 'Farmer Details'}
          </span>
        </div>
      </div>
      
      <!-- Tab Headers -->
      <div style="display: flex; border-bottom: 2px solid #dee2e6; background: #f8f9fa;">
        <button class="tab-button active" 
                onclick="document.getElementById('${tab1Id}').style.display='block'; document.getElementById('${tab2Id}').style.display='none'; this.classList.add('active'); this.nextElementSibling.classList.remove('active'); this.style.backgroundColor='#007bff'; this.style.color='white'; this.nextElementSibling.style.backgroundColor='#f8f9fa'; this.nextElementSibling.style.color='#495057';"
                style="flex: 1; padding: 12px; border: none; background: #007bff; color: white; font-weight: 600; cursor: pointer; border-bottom: 3px solid #0056b3; transition: all 0.2s;">
          📋 Details
        </button>
        <button class="tab-button"
                onclick="document.getElementById('${tab2Id}').style.display='block'; document.getElementById('${tab1Id}').style.display='none'; this.classList.add('active'); this.previousElementSibling.classList.remove('active'); this.style.backgroundColor='#007bff'; this.style.color='white'; this.previousElementSibling.style.backgroundColor='#f8f9fa'; this.previousElementSibling.style.color='#495057';"
                style="flex: 1; padding: 12px; border: none; background: #f8f9fa; color: #495057; font-weight: 600; cursor: pointer; border-bottom: 3px solid transparent; transition: all 0.2s;">
          🖼️ ${scheme === 12 && farmerData.surveyImageList && farmerData.surveyImageList.length > 0 ? `Image (${farmerData.surveyImageList.length})` : 'Image'}
        </button>
      </div>

      <!-- Tab 1: Details Content -->
      <div id="${tab1Id}" style="display: block; max-height: 600px; overflow-y: auto;">
        <div class="detail-section" style="margin: 15px;">
          <div style="border: 1px solid #e9ecef; border-radius: 6px; overflow: scroll; max-height:300px;">
            <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
  `;

  desiredFields.forEach(field => {
  let value = farmerData[field.key];
  
  // Handle nested fields
  if (scheme !== 12) {
    if (field.key === 'finYear' || field.key === 'seasonName' || 
        field.key === 'cropGroupName' || field.key === 'cropName') {
      if (farmerData.farmerCropDetailsExtsList && 
          farmerData.farmerCropDetailsExtsList.length > 0) {
        value = farmerData.farmerCropDetailsExtsList[0][field.key];
      } 
    }
    
    else if (field.key === 'farmerType' && value !== undefined) {
      const farmerTypeMap = {
        1: 'Individual Farmer',
        2: 'Group/FPO',
        3: 'Joint Liability Group',
      };
      value = farmerTypeMap[value] || value;
    }
  }
  
  // Pass field.key to check if it's clickable
  html += addTableRow(field.label, value, field.key, farmerData);
});

  if (scheme === 12 && farmerData.surveyPolygonList && farmerData.surveyPolygonList.length > 0) {
    html += addTableRow('Polygon Count', farmerData.surveyPolygonList.length);
  }

  html += `
            </table>
          </div>
        </div>
        
        <div style="text-align: right; font-size: 11px; color: #999; padding: 5px 15px 15px;">
          ${scheme === 12 ? 'Survey Code' : 'Farmer Code'}: ${scheme === 12 ? (farmerData.surveyCode || 'N/A') : (farmerData.farmerCode || 'N/A')}
        </div>
      </div>

      <!-- Tab 2: Image Content -->
      <div id="${tab2Id}" style="display: none; padding: 20px; text-align: center; background: #f8f9fa; max-height: 600px; overflow-y: auto;">
  `;

  // Image display logic
  if (imagePath && imagePath !== "N/A" && imagePath !== "") {
  let imageUrl;
  
  // Handle different schemes
 if (isFullUrl) {
    imageUrl = imagePath;
  } else if (scheme === 5) {
    imageUrl = imagePath;
  } else if (scheme === 12) {
    imageUrl = imagePath;
  } else if (scheme === 15 && isFullUrl) {
    imageUrl = imagePath;
  } else if (scheme === 22) {
    imageUrl = `${baseImageUrl}${imagePath}`;
  }  else if (scheme === 24 && isFullUrl) {
    // AIF - already full URL
    imageUrl = imagePath;
  }else if ([70, 71, 73, 74, 75].includes(scheme)) {
    // M&T schemes - already full URLs
    imageUrl = imagePath;
  }else if (scheme === 27 || scheme === 40) {
    // For SMAM, check if it's a full URL or needs base URL
    if (isFullUrl) {
      imageUrl = imagePath;
    } else {
      // Don't encode if it might already be encoded or has special characters
      imageUrl = `${baseImageUrl}${imagePath}`;
    }
    console.log('🔍 MT SMAM - Final image URL:', imageUrl);
  } else {
    imageUrl = `${baseImageUrl}${encodeURIComponent(imagePath)}`;
  }
  if ([70, 71, 73, 74, 75].includes(scheme) && farmerData.imagePath1) {
  const allImages = farmerData.imagePath1.split(',').map(s => s.trim()).filter(Boolean);
  const lats = (farmerData.imageLat || '').split(',').map(s => s.trim());
  const lngs = (farmerData.imageLong || '').split(',').map(s => s.trim());

  html += `
    <div style="display: flex; flex-direction: column; gap: 12px;">
      ${allImages.map((img, i) => `
        <div style="position: relative; border: 2px solid #dee2e6; border-radius: 8px; overflow: hidden; background: white;">
          <div style="padding: 6px 10px; background: #f8f9fa; font-size: 12px; color: #495057; border-bottom: 1px solid #e9ecef;">
            Image ${i + 1} of ${allImages.length}
            ${lats[i] && lngs[i] ? ` &nbsp;|&nbsp; Lat: ${lats[i]}, Long: ${lngs[i]}` : ''}
          </div>
          <img src="${img}" 
               alt="Image ${i + 1}"
               style="max-width: 100%; max-height: 400px; display: block; margin: 0 auto;"
               onerror="this.parentElement.innerHTML='<div style=\\'padding:20px;color:#721c24;background:#f8d7da;text-align:center;\\'>Image ${i + 1} not available</div>';" />
        </div>
      `).join('')}
    </div>
  `;
}
    const imageViewerId = `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    html += `
      <div style="margin-bottom: 15px;">
        <!-- Control buttons -->
        <div style="display: flex; justify-content: center; gap: 8px; margin-bottom: 15px; flex-wrap: wrap;">
          <button onclick="(function(){ 
            var img = document.getElementById('${imageViewerId}');
            if(!img) return;
            var scale = parseFloat(img.getAttribute('data-scale') || '1');
            scale = Math.min(scale + 0.2, 3);
            img.setAttribute('data-scale', scale);
            img.style.transform = 'scale(' + scale + ') rotate(' + (img.getAttribute('data-rotate') || '0') + 'deg) translate(' + (img.getAttribute('data-pan-x') || '0') + 'px, ' + (img.getAttribute('data-pan-y') || '0') + 'px)';
          })()" 
                  style="background: #007bff; color: white; border: none; border-radius: 4px; width: 36px; height: 36px; cursor: pointer; font-size: 18px; display: inline-flex; align-items: center; justify-content: center;"
                  title="Zoom In">
            🔍
          </button>
          <button onclick="(function(){ 
            var img = document.getElementById('${imageViewerId}');
            if(!img) return;
            var scale = parseFloat(img.getAttribute('data-scale') || '1');
            scale = Math.max(scale - 0.2, 0.5);
            img.setAttribute('data-scale', scale);
            img.style.transform = 'scale(' + scale + ') rotate(' + (img.getAttribute('data-rotate') || '0') + 'deg) translate(' + (img.getAttribute('data-pan-x') || '0') + 'px, ' + (img.getAttribute('data-pan-y') || '0') + 'px)';
          })()" 
                  style="background: #6c757d; color: white; border: none; border-radius: 4px; width: 36px; height: 36px; cursor: pointer; font-size: 18px; display: inline-flex; align-items: center; justify-content: center;"
                  title="Zoom Out">
            🔎
          </button>
          <button onclick="(function(){ 
            var img = document.getElementById('${imageViewerId}');
            if(!img) return;
            var rotate = parseFloat(img.getAttribute('data-rotate') || '0');
            rotate = (rotate - 90) % 360;
            img.setAttribute('data-rotate', rotate);
            img.style.transform = 'scale(' + (img.getAttribute('data-scale') || '1') + ') rotate(' + rotate + 'deg) translate(' + (img.getAttribute('data-pan-x') || '0') + 'px, ' + (img.getAttribute('data-pan-y') || '0') + 'px)';
          })()" 
                  style="background: #28a745; color: white; border: none; border-radius: 4px; width: 36px; height: 36px; cursor: pointer; font-size: 18px; display: inline-flex; align-items: center; justify-content: center;"
                  title="Rotate Left">
            ↺
          </button>
          <button onclick="(function(){ 
            var img = document.getElementById('${imageViewerId}');
            if(!img) return;
            var rotate = parseFloat(img.getAttribute('data-rotate') || '0');
            rotate = (rotate + 90) % 360;
            img.setAttribute('data-rotate', rotate);
            img.style.transform = 'scale(' + (img.getAttribute('data-scale') || '1') + ') rotate(' + rotate + 'deg) translate(' + (img.getAttribute('data-pan-x') || '0') + 'px, ' + (img.getAttribute('data-pan-y') || '0') + 'px)';
          })()" 
                  style="background: #28a745; color: white; border: none; border-radius: 4px; width: 36px; height: 36px; cursor: pointer; font-size: 18px; display: inline-flex; align-items: center; justify-content: center;"
                  title="Rotate Right">
            ↻
          </button>
          <button onclick="(function(){ 
            var img = document.getElementById('${imageViewerId}');
            if(!img) return;
            img.setAttribute('data-scale', '1');
            img.setAttribute('data-rotate', '0');
            img.setAttribute('data-pan-x', '0');
            img.setAttribute('data-pan-y', '0');
            img.style.transform = 'scale(1) rotate(0deg) translate(0px, 0px)';
          })()" 
                  style="background: #dc3545; color: white; border: none; border-radius: 4px; width: 36px; height: 36px; cursor: pointer; font-size: 18px; display: inline-flex; align-items: center; justify-content: center;"
                  title="Reset">
            ⟲
          </button>
        </div>
        
        <!-- Image container -->
        <div style="position: relative; display: inline-block; overflow: hidden; border-radius: 8px; border: 2px solid #dee2e6; box-shadow: 0 2px 8px rgba(0,0,0,0.1); background: white;">
          <img id="${imageViewerId}" 
               src="${imageUrl}" 
               alt="${scheme === 12 ? 'Survey Image' : 'Farmer Image'}" 
               data-scale="1"
               data-rotate="0"
               data-pan-x="0"
               data-pan-y="0"
               style="max-width: 100%; max-height: 500px; transform: scale(1) rotate(0deg) translate(0px, 0px); transition: transform 0.1s ease; display: block; margin: 0 auto; cursor: move;"
               ondblclick="document.getElementById('${modalId}').style.display='block'"
               onerror="this.onerror=null; this.parentElement.innerHTML='<div style=\\'padding: 40px; background: #f8d7da; color: #721c24; border-radius: 8px;\\'>Image not available</div>';"
               onmousedown="(function(e){
                 e.preventDefault();
                 var img = e.target;
                 var startX = e.clientX;
                 var startY = e.clientY;
                 var startPanX = parseFloat(img.getAttribute('data-pan-x') || '0');
                 var startPanY = parseFloat(img.getAttribute('data-pan-y') || '0');
                 
                 function onMouseMove(e) {
                   var dx = e.clientX - startX;
                   var dy = e.clientY - startY;
                   var newPanX = startPanX + dx;
                   var newPanY = startPanY + dy;
                   img.setAttribute('data-pan-x', newPanX);
                   img.setAttribute('data-pan-y', newPanY);
                   img.style.transform = 'scale(' + (img.getAttribute('data-scale') || '1') + ') rotate(' + (img.getAttribute('data-rotate') || '0') + 'deg) translate(' + newPanX + 'px, ' + newPanY + 'px)';
                 }
                 
                 function onMouseUp() {
                   document.removeEventListener('mousemove', onMouseMove);
                   document.removeEventListener('mouseup', onMouseUp);
                 }
                 
                 document.addEventListener('mousemove', onMouseMove);
                 document.addEventListener('mouseup', onMouseUp);
               })(event)">
        </div>
        
        ${scheme === 12 && farmerData.surveyImageList && farmerData.surveyImageList.length > 1 ? `
          <div style="margin-top: 10px; font-size: 11px; color: #666;">
            Showing image 1 of ${farmerData.surveyImageList.length}
          </div>
        ` : ''}
      </div>
    `;
  } else {
    html += `
      <div style="padding: 60px 20px; color: #6c757d; text-align: center;">
        <i class="bi bi-image" style="font-size: 48px; display: block; margin-bottom: 15px;"></i>
        <p>No image available for this ${scheme === 12 ? 'survey' : 'farmer'}</p>
      </div>
    `;
  }

  html += `</div>`; // Close image tab
  
  html += `
    <script>
      (function() {
        setTimeout(function() {
          var popup = document.getElementById('${popupId}');
          if (popup && window.makePopupDraggable) {
            if (popup._dragCleanup) {
              popup._dragCleanup();
            }
            window.makePopupDraggable(popup);
          }
        }, 200);
        
        var modal = document.getElementById('${modalId}');
        if (modal) {
          modal.addEventListener('click', function(e) {
            if (e.target === this) {
              this.style.display = 'none';
            }
          });
          
          document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape') {
              var modal = document.getElementById('${modalId}');
              if (modal) modal.style.display = 'none';
              
              var popup = document.querySelector('.leaflet-popup');
              if (popup) {
                var closeBtn = popup.querySelector('.leaflet-popup-close-button');
                if (closeBtn) closeBtn.click();
              }
            }
          });
        }
      })();
    </script>
  `;

  // Enhanced Image Modal
  html += `
    <div id="${modalId}" style="display: none; position: fixed; z-index: 9999; left: 0; top: 0; width: 100%; height: 100%; background-color: rgba(0,0,0,0.95);">
      <div style="position: relative; width: 100%; height: 100%; display: flex; flex-direction: column; justify-content: center; align-items: center; padding: 20px;">
        <div style="position: absolute; top: 20px; right: 30px; z-index: 10000;">
          <button onclick="document.getElementById('${modalId}').style.display='none'" 
                  style="background: #dc3545; color: white; border: none; border-radius: 50%; width: 40px; height: 40px; font-size: 20px; cursor: pointer; display: flex; align-items: center; justify-content: center;"
                  title="Close">
            ×
          </button>
        </div>
        
        <div style="position: absolute; top: 20px; left: 50%; transform: translateX(-50%); z-index: 10000; display: flex; gap: 8px; background: rgba(255,255,255,0.2); padding: 10px; border-radius: 8px; backdrop-filter: blur(5px);">
          <button onclick="(function(){
            var img = document.getElementById('${modalId}-image');
            if(!img) return;
            var scale = parseFloat(img.getAttribute('data-scale') || '1');
            scale = Math.min(scale + 0.2, 3);
            img.setAttribute('data-scale', scale);
            img.style.transform = 'scale(' + scale + ') rotate(' + (img.getAttribute('data-rotate') || '0') + 'deg) translate(' + (img.getAttribute('data-pan-x') || '0') + 'px, ' + (img.getAttribute('data-pan-y') || '0') + 'px)';
          })()" 
                  style="background: #007bff; color: white; border: none; border-radius: 4px; width: 40px; height: 40px; cursor: pointer; font-size: 20px; display: inline-flex; align-items: center; justify-content: center;"
                  title="Zoom In">
            🔍
          </button>
          <button onclick="(function(){
            var img = document.getElementById('${modalId}-image');
            if(!img) return;
            var scale = parseFloat(img.getAttribute('data-scale') || '1');
            scale = Math.max(scale - 0.2, 0.5);
            img.setAttribute('data-scale', scale);
            img.style.transform = 'scale(' + scale + ') rotate(' + (img.getAttribute('data-rotate') || '0') + 'deg) translate(' + (img.getAttribute('data-pan-x') || '0') + 'px, ' + (img.getAttribute('data-pan-y') || '0') + 'px)';
          })()" 
                  style="background: #6c757d; color: white; border: none; border-radius: 4px; width: 40px; height: 40px; cursor: pointer; font-size: 20px; display: inline-flex; align-items: center; justify-content: center;"
                  title="Zoom Out">
            🔎
          </button>
          <button onclick="(function(){
            var img = document.getElementById('${modalId}-image');
            if(!img) return;
            var rotate = parseFloat(img.getAttribute('data-rotate') || '0');
            rotate = (rotate - 90) % 360;
            img.setAttribute('data-rotate', rotate);
            img.style.transform = 'scale(' + (img.getAttribute('data-scale') || '1') + ') rotate(' + rotate + 'deg) translate(' + (img.getAttribute('data-pan-x') || '0') + 'px, ' + (img.getAttribute('data-pan-y') || '0') + 'px)';
          })()" 
                  style="background: #28a745; color: white; border: none; border-radius: 4px; width: 40px; height: 40px; cursor: pointer; font-size: 20px; display: inline-flex; align-items: center; justify-content: center;"
                  title="Rotate Left">
            ↺
          </button>
          <button onclick="(function(){
            var img = document.getElementById('${modalId}-image');
            if(!img) return;
            var rotate = parseFloat(img.getAttribute('data-rotate') || '0');
            rotate = (rotate + 90) % 360;
            img.setAttribute('data-rotate', rotate);
            img.style.transform = 'scale(' + (img.getAttribute('data-scale') || '1') + ') rotate(' + rotate + 'deg) translate(' + (img.getAttribute('data-pan-x') || '0') + 'px, ' + (img.getAttribute('data-pan-y') || '0') + 'px)';
          })()" 
                  style="background: #28a745; color: white; border: none; border-radius: 4px; width: 40px; height: 40px; cursor: pointer; font-size: 20px; display: inline-flex; align-items: center; justify-content: center;"
                  title="Rotate Right">
            ↻
          </button>
          <button onclick="(function(){
            var img = document.getElementById('${modalId}-image');
            if(!img) return;
            img.setAttribute('data-scale', '1');
            img.setAttribute('data-rotate', '0');
            img.setAttribute('data-pan-x', '0');
            img.setAttribute('data-pan-y', '0');
            img.style.transform = 'scale(1) rotate(0deg) translate(0px, 0px)';
          })()" 
                  style="background: #dc3545; color: white; border: none; border-radius: 4px; width: 40px; height: 40px; cursor: pointer; font-size: 20px; display: inline-flex; align-items: center; justify-content: center;"
                  title="Reset">
            ⟲
          </button>
        </div>
        
        <div style="max-width: 90%; max-height: 80%; display: flex; justify-content: center; align-items: center; overflow: hidden;">
          <img id="${modalId}-image" 
             src="${imagePath ? (isFullUrl || imagePath.startsWith('http') ? imagePath : `${baseImageUrl}${encodeURIComponent(imagePath)}`) : ''}"  
               data-scale="1"
               data-rotate="0"
               data-pan-x="0"
               data-pan-y="0"
               style="max-width: 100%; max-height: 100%; object-fit: contain; transform: scale(1) rotate(0deg) translate(0px, 0px); transition: transform 0.1s ease; border-radius: 8px; box-shadow: 0 8px 30px rgba(0,0,0,0.6); cursor: move;"
               alt="Full size ${scheme === 12 ? 'survey' : 'farmer'} image"
               onerror="this.parentElement.innerHTML='<div style=\\'color: white; text-align: center;\\'>Image failed to load</div>'"
               onmousedown="(function(e){
                 e.preventDefault();
                 var img = e.target;
                 var startX = e.clientX;
                 var startY = e.clientY;
                 var startPanX = parseFloat(img.getAttribute('data-pan-x') || '0');
                 var startPanY = parseFloat(img.getAttribute('data-pan-y') || '0');
                 
                 function onMouseMove(e) {
                   var dx = e.clientX - startX;
                   var dy = e.clientY - startY;
                   var newPanX = startPanX + dx;
                   var newPanY = startPanY + dy;
                   img.setAttribute('data-pan-x', newPanX);
                   img.setAttribute('data-pan-y', newPanY);
                   img.style.transform = 'scale(' + (img.getAttribute('data-scale') || '1') + ') rotate(' + (img.getAttribute('data-rotate') || '0') + 'deg) translate(' + newPanX + 'px, ' + newPanY + 'px)';
                 }
                 
                 function onMouseUp() {
                   document.removeEventListener('mousemove', onMouseMove);
                   document.removeEventListener('mouseup', onMouseUp);
                 }
                 
                 document.addEventListener('mousemove', onMouseMove);
                 document.addEventListener('mouseup', onMouseUp);
               })(event)">
        </div>
      </div>
    </div>
  `;

  return html;
};
// Parse polygon coordinates
const parsePolygonCoordinates = (polygonShape) => {
  if (!polygonShape || polygonShape === "null" || polygonShape === "N/A") {
    return null;
  }

  try {
    let cleanedShape = polygonShape.toString().trim();
    cleanedShape = cleanedShape.replace(/^["']|["']$/g, '').replace(/\\"/g, '"');
    
    if (!cleanedShape || cleanedShape === '[]' || cleanedShape === '{}') {
      return null;
    }

    let parsed;
    
    try {
      parsed = JSON.parse(cleanedShape);
    } catch (jsonError) {
      const coordMatches = cleanedShape.match(/[-+]?[0-9]*\.?[0-9]+/g);
      if (coordMatches && coordMatches.length >= 6) {
        const coords = [];
        for (let i = 0; i < coordMatches.length; i += 2) {
          const lat = parseFloat(coordMatches[i]);
          const lng = parseFloat(coordMatches[i + 1]);
          if (!isNaN(lat) && !isNaN(lng)) {
            coords.push({ lat, lng });
          }
        }
        parsed = coords.length >= 3 ? coords : null;
      } else {
        return null;
      }
    }

    if (!Array.isArray(parsed) || parsed.length === 0) {
      return null;
    }

    const coordinates = parsed.map(point => {
      let lat, lng;
      
      if (typeof point === 'object') {
        lat = parseFloat(point.lat || point.latitude || point.y || point[0]);
        lng = parseFloat(point.lng || point.longitude || point.lon || point.x || point[1]);
      } else if (Array.isArray(point)) {
        lat = parseFloat(point[0]);
        lng = parseFloat(point[1]);
      }
      
      if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return null;
      }
      
      return [lat, lng];
    }).filter(coord => coord !== null);

    if (coordinates.length < 3) {
      return null;
    }

    return coordinates;
    
  } catch (error) {
    console.error('Error parsing polygon coordinates:', error);
    return null;
  }
};

const PolygonLayer = ({
  selectedScheme,
  selectedDistrict,
  selectedFinancialYear = "",
  onPolygonsLoaded,
  schemeColors = {},
  onDistrictSelect,
  userVisible,
  setUserVisible,
  displayAnalytics,
  setDisplayAnalytics,
  setShowATMAPopup,        
  setATMAPopupData,       
  setShowBeneficiaryModal, 
  setSelectedBeneficiaryData,
  setIsSidebarMinimized,
}) => {
  const map = useMap();
  const [polygons, setPolygons] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [farmerDetailsLoading, setFarmerDetailsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const polygonLayersRef = useRef([]);
  const markerLayersRef = useRef([]);
useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  // Get scheme code
  const getSchemeCode = useCallback(() => {
    if (!selectedScheme || !selectedScheme.schemeName) {
      return null;
    }
    
    const code = getSchemeCodeByName(selectedScheme.schemeName);
    
    if (code && typeof code === 'object' && code.schemeCode) {
      return code.schemeCode.toString();
    }
    
    return code ? code.toString() : null;
  }, [selectedScheme]);

  // Get financial year parameter
  const getFinancialYearParam = useCallback(() => {
    if (selectedFinancialYear === "ALL" || !selectedFinancialYear) {
      return "";
    }
    return selectedFinancialYear;
  }, [selectedFinancialYear]);

  // Zoom to polygon
  const zoomToPolygon = useCallback((polygonData) => {
    const coordinates = parsePolygonCoordinates(polygonData.polygonShape);
    if (!coordinates) return;

    const tempPolygon = L.polygon(coordinates);
    const bounds = tempPolygon.getBounds();
    
    if (bounds.isValid()) {
      map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 18,
        duration: 1
      });
    }
  }, [map]);



// In PolygonLayer.jsx, update the handlePolygonClick function
const handlePolygonClick = useCallback(async (polygonData, polygonLayer) => {
  console.log('🎯 Polygon clicked:', {
    farmerCode: polygonData.farmerCode,
    schemeCode: polygonData.schemeCode,
    subSchemeCode: polygonData.subSchemeCode,
    selectedScheme: selectedScheme,
    cropDemonstrationCode: polygonData.cropDemonstrationCode,
    isATMA: polygonData.schemeCode === 20
  });
  try {
    const coordinates = parsePolygonCoordinates(polygonData.polygonShape);
    if (coordinates && map) {
      console.log('🔍 Zooming to polygon bounds');
      const tempPolygon = L.polygon(coordinates);
      const bounds = tempPolygon.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, {
          padding: [50, 50],
          maxZoom: 18,
          duration: 1
        });
      }
    }
  } catch (zoomError) {
    console.warn('Could not zoom to polygon:', zoomError);
  }
  
  const schemeCodeNum = parseInt(polygonData.schemeCode || getSchemeCode());
  const isATMA = schemeCodeNum === 20;
  const identifier = isATMA ? polygonData.cropDemonstrationCode : polygonData.farmerCode;
  
  if (!identifier) {
    if (isATMA && setATMAPopupData && setShowATMAPopup) {
      setATMAPopupData({
        error: `No ${isATMA ? 'demonstration code' : 'farmer code'} found`
      });
      setShowATMAPopup(true);
      // OPEN SIDEBAR when showing ATMA popup
      if (setIsSidebarMinimized) {
        setIsSidebarMinimized(false);
      }
    }
    return;
  }

  

  setFarmerDetailsLoading(true);

  try {
    const schemeCode = getSchemeCode();
    const subSchemeCode = polygonData.subSchemeCode || selectedScheme?.subSchemeCode;
    const stateCode = parseInt(
    polygonData.stateCode || 
    polygonData.State_LGD || 
    polygonData.stateLGD || 
    selectedDistrict?.properties?.STATE_LGD || 
  selectedDistrict?.properties?.State_LGD ||
  selectedDistrict?.properties?.stateCode ||
  selectedDistrict?.properties?.STATE_CODE ||
  selectedDistrict?.properties?.st_code ||
  0
  );
    const details = await fetchFarmerDetails(identifier, schemeCode, subSchemeCode,stateCode);
    
    if (isATMA && details.isATMA && setATMAPopupData && setShowATMAPopup) {
      // Send data to sidebar
      setATMAPopupData({
        demonstrationData: {
          cropDemonstrationCode: details.cropDemonstrationCode,
          demoType: polygonData.demoType,
          financialYear: details.dataContent?.[0]?.financialYear,
          season: details.dataContent?.[0]?.season
        },
        beneficiaries: details.dataContent || []
      });
      setShowATMAPopup(true);
      
      // OPEN SIDEBAR when showing ATMA popup
      if (setIsSidebarMinimized) {
        setIsSidebarMinimized(false);
      }
      
      if (polygonLayer && polygonLayer.closePopup) {
        polygonLayer.closePopup();
      }
    } else {
      const detailedPopupContent = formatFarmerDetails(details, schemeCode, { 
        schemeCode: schemeCode, 
        subSchemeCode: subSchemeCode 
      });
      polygonLayer.getPopup().setContent(detailedPopupContent);
      // NO zooming here either
      polygonLayer.openPopup();
    }
    
  } catch (error) {
    console.error('❌ Error in polygon click handler:', error);
    
    if (isATMA && setATMAPopupData && setShowATMAPopup) {
      setATMAPopupData({
        error: `Failed to load demonstration details: ${error.message}`
      });
      setShowATMAPopup(true);
      // OPEN SIDEBAR when showing ATMA popup
      if (setIsSidebarMinimized) {
        setIsSidebarMinimized(false);
      }
    }
  } finally {
    setFarmerDetailsLoading(false);
  }
}, [getSchemeCode, selectedScheme, setATMAPopupData, setShowATMAPopup, setIsSidebarMinimized, setFarmerDetailsLoading]);

const createPolygonLayer = useCallback((polygonData) => {
  const coordinates = parsePolygonCoordinates(polygonData.polygonShape);
  if (!coordinates) {
    return null;
  }

  const color = getSchemeColor(polygonData.schemeCode, polygonData.subSchemeCode);
  
  const polygon = L.polygon(coordinates, {
    color: color,
    weight: 3,
    opacity: 0.9,
    fillColor: color,
    fillOpacity: 0.4,
    className: 'survey-polygon'
  });

  // Create popup with minimal content (will be updated on click)
  const popup = L.popup({
    maxWidth: 800,
    minWidth: 400,
    maxHeight: 600,
    autoPan: true,
    closeOnClick: false,
    closeButton: true,
    className: 'farmer-details-popup-container',
    closeOnEscapeKey: false,    
    autoClose: false
  }).setContent('<div style="padding: 20px; text-align: center;">Loading details...</div>');
  
  polygon.bindPopup(popup);

  polygon.on('click', async function() {
    console.log('Polygon clicked, fetching details...');
    await handlePolygonClick(polygonData, this);
  });

  polygon.on('mouseover', function() {
    this.setStyle({ weight: 4, fillOpacity: 0.6 });
  });
  
  polygon.on('mouseout', function() {
    this.setStyle({ weight: 3, fillOpacity: 0.4 });
  });

  return polygon;
}, [handlePolygonClick]);

const handleMarkerClick = useCallback(async (polygonData, marker, lat, lng) => {
  console.log('🔍 Marker click handler - Fetching details for scheme:', {
    schemeCode: polygonData.schemeCode,
    farmerCode: polygonData.farmerCode,
    chcId: polygonData.chcId,
    cropDemonstrationCode: polygonData.cropDemonstrationCode,
    subSchemeCode: polygonData.subSchemeCode
  });
  if (map && lat && lng) {
    console.log('🔍 Zooming to marker location:', { lat, lng });
    map.setView([lat, lng], 18, {
      animate: true,
      duration: 0.5
    });
  }
  // Determine the identifier based on scheme type
  const schemeCodeNum = parseInt(polygonData.schemeCode);
  let farmerIdentifier = null;
  
  // Handle different scheme types
  switch(schemeCodeNum) {
    case 20: // ATMA
      farmerIdentifier = polygonData.cropDemonstrationCode;
      break;
    case 33: // AMI
      farmerIdentifier = polygonData.farmerCode || polygonData.projectID;
      break;
    case 40: // MT-CRM
    case 27: // MT-SMAM
      farmerIdentifier = polygonData.chcId || polygonData.farmerCode;
      break;
    case 4: // Seed Hub (old)
    case 29: // Seed Hub (new)
      farmerIdentifier = polygonData.id || polygonData.farmerCode;
      break;
    case 21: // NBM
      farmerIdentifier = polygonData.farmerCode;
      break;
    case 53: // NSR
      farmerIdentifier = polygonData.farmerCode;
      break;
    default:
      // For all other schemes, try farmerCode first, then fallback to other identifiers
      farmerIdentifier = polygonData.farmerCode || 
                        polygonData.farmerCode || 
                        polygonData.chcId || 
                        polygonData.cropDemonstrationCode ||
                        polygonData.id;
      break;
  }
  
  // If we still don't have an identifier, try to find it in the data
  if (!farmerIdentifier) {
    console.warn('⚠️ No identifier found, searching in polygonData:', polygonData);
    // Try to find any field that might contain an ID
    const possibleIdFields = ['farmerCode', 'chcId', 'cropDemonstrationCode', 'id', 'projectID', 'surveyCode'];
    for (const field of possibleIdFields) {
      if (polygonData[field]) {
        farmerIdentifier = polygonData[field];
        console.log(`✅ Found identifier in field '${field}':`, farmerIdentifier);
        break;
      }
    }
  }
  
  if (!farmerIdentifier) {
    console.error('❌ No identifier found for scheme:', schemeCodeNum);
    const errorContent = `
      <div style="text-align: center; padding: 15px;">
        <h6>No Identifier Found</h6>
        <div style="color: orange; padding: 10px;">
          <i class="bi bi-exclamation-triangle"></i>
          <p>Could not find identifier for this ${schemeCodeNum === 20 ? 'demonstration' : 'farmer'}</p>
          <small style="display: block; margin-top: 8px; color: #666;">
            Available fields: ${Object.keys(polygonData).filter(k => polygonData[k]).join(', ')}
          </small>
        </div>
        <button onclick="this.closest('.leaflet-popup').close()" 
                style="margin-top: 10px; background: #6c757d; color: white; border: none; border-radius: 4px; padding: 5px 10px; cursor: pointer;">
          Close
        </button>
      </div>
    `;
    marker.getPopup().setContent(errorContent);
    marker.openPopup();
    return;
  }

  setFarmerDetailsLoading(true);

  try {
    const schemeCode = polygonData.schemeCode;
    const subSchemeCode = polygonData.subSchemeCode || selectedScheme?.subSchemeCode;
    
    console.log('📡 Fetching details:', {
      identifier: farmerIdentifier,
      schemeCode: schemeCode,
      subSchemeCode: subSchemeCode,
      schemeCodeNum: schemeCodeNum
    });

    // Show loading state
    const loadingContent = `
      <div style="text-align: center; padding: 20px;">
        <div class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p style="margin-top: 10px; font-size: 13px;">Loading details for ${getSchemeNameByCode(schemeCode, subSchemeCode)}...</p>
        <small style="color: #666; display: block; margin-top: 5px;">Identifier: ${farmerIdentifier}</small>
      </div>
    `;
    
    marker.getPopup().setContent(loadingContent);
    marker.openPopup();
 const stateCode = parseInt(
    polygonData.stateCode || 
    polygonData.State_LGD || 
    polygonData.stateLGD || 
    selectedDistrict?.properties?.STATE_LGD || 
  selectedDistrict?.properties?.State_LGD ||
  selectedDistrict?.properties?.stateCode ||
  selectedDistrict?.properties?.STATE_CODE ||
  selectedDistrict?.properties?.st_code ||
  0
  );
    // Fetch details
    const details = await fetchFarmerDetails(
      farmerIdentifier, 
      schemeCode, 
      subSchemeCode,
      stateCode 
    );
    
    console.log('✅ Details received:', {
      hasData: !!details,
      dataContent: details.dataContent,
      isAMI: details.isAMI,
      isATMA: details.isATMA,
      recordCount: details.recordCount
    });

    // Check if we got valid data
    const hasData = details.dataContent && 
                   (Array.isArray(details.dataContent) ? details.dataContent.length > 0 : true);
    
    if (!hasData) {
      // Show "No Data" message but keep popup open
      const noDataContent = `
        <div style="text-align: center; padding: 20px;">
          <h6 style="color: #dc3545; margin-bottom: 10px;">No Data Available</h6>
          <p style="color: #666;">No details found for identifier: ${farmerIdentifier}</p>
          <small style="color: #999; display: block; margin-top: 10px;">
            Scheme: ${getSchemeNameByCode(schemeCode, subSchemeCode)}
          </small>
          <button onclick="this.closest('.leaflet-popup').close()" 
                  style="margin-top: 15px; background: #6c757d; color: white; border: none; border-radius: 4px; padding: 5px 15px; cursor: pointer;">
            Close
          </button>
        </div>
      `;
      marker.getPopup().setContent(noDataContent);
      marker.openPopup();
      return;
    }

    // Format and display the details
    const detailedContent = formatFarmerDetails(details, schemeCode, { 
      schemeCode: schemeCode, 
      subSchemeCode: subSchemeCode 
    });
    
    // Update popup with detailed content
    marker.getPopup().setContent(detailedContent);
    
    // Force refresh of popup
    if (!marker.isPopupOpen()) {
      marker.openPopup();
    } else {
      marker.closePopup();
      setTimeout(() => {
        marker.openPopup();
      }, 100);
    }
    
  } catch (error) {
    console.error('❌ Error fetching details:', error);
    
    const schemeName = getSchemeNameByCode(polygonData.schemeCode, polygonData.subSchemeCode);
    
    const errorContent = `
      <div style="text-align: center; padding: 15px;">
        <h6 style="color: #dc3545;">Error Loading Details</h6>
        <p><strong>Scheme:</strong> ${schemeName}</p>
        <p><strong>Identifier:</strong> ${farmerIdentifier}</p>
        <div style="color: #721c24; background: #f8d7da; padding: 10px; border-radius: 4px; margin: 10px 0;">
          <p style="margin: 0;">${error.message}</p>
        </div>
        <button onclick="this.closest('.leaflet-popup').close()" 
                style="margin-top: 10px; background: #6c757d; color: white; border: none; border-radius: 4px; padding: 5px 15px; cursor: pointer;">
          Close
        </button>
      </div>
    `;
    marker.getPopup().setContent(errorContent);
    
    if (!marker.isPopupOpen()) {
      marker.openPopup();
    }
  } finally {
    setFarmerDetailsLoading(false);
  }
}, [selectedScheme, setFarmerDetailsLoading]);

const createSurveyorMarker = useCallback((polygonData) => {
  const lat = parseFloat(polygonData.surveyorLat);
  const lng = parseFloat(polygonData.surveyorLong);
  const markerLat = lat;
  const markerLng = lng;
  
  if (isNaN(lat) || isNaN(lng)) {
    console.log('❌ Invalid coordinates for marker:', { lat, lng, polygonData });
    return null;
  }

  const color = getSchemeColor(polygonData.schemeCode, polygonData.subSchemeCode);
  const schemeCode = parseInt(polygonData.schemeCode);
  
  console.log('🎨 Creating marker for scheme:', { 
    schemeCode, 
    farmerCode: polygonData.farmerCode,
    hasPolygon: !!polygonData.polygonShape && polygonData.polygonShape !== "null" && polygonData.polygonShape !== "N/A"
  });
  
  // Determine marker size based on scheme
  const markerSize = 16;
  const iconSize = 20;
  const iconAnchor = 10;
  
  // Check if should have pulse animation (for special schemes)
  const hasPulseAnimation = schemeCode === 4 || schemeCode === 29 || schemeCode === 20 || schemeCode === 33;
  
  const icon = L.divIcon({
    className: 'surveyor-marker',
    html: `
      <div style="
        background-color: ${color};
        width: ${markerSize}px;
        height: ${markerSize}px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        cursor: pointer;
        ${hasPulseAnimation ? 'animation: pulse 1.5s infinite;' : ''}
      "></div>
      ${hasPulseAnimation ? '<style>@keyframes pulse { 0% { box-shadow: 0 0 0 0 rgba(255,255,255,0.7); } 70% { box-shadow: 0 0 0 10px rgba(255,255,255,0); } 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); } }</style>' : ''}
    `,
    iconSize: [iconSize, iconSize],
    iconAnchor: [iconAnchor, iconAnchor]
  });

  const marker = L.marker([lat, lng], { icon, zIndexOffset: 1000 });
  
  // Create popup with proper options
  const popup = L.popup({
    maxWidth: 800,
    minWidth: 400,
    maxHeight: 600,
    autoPan: true,
    closeOnClick: false,
    closeButton: true,
    className: 'farmer-details-popup-container',
    closeOnEscapeKey: false,
    autoClose: false,
    offset: [0, -15]
  }).setContent('<div style="padding: 20px; text-align: center; min-height: 80px;">Click to load details...</div>');
  
  marker.bindPopup(popup);
  
  // Click handler for marker - THIS WILL WORK FOR ALL SCHEMES
  marker.on('click', async function(e) {
    // Stop event propagation
    L.DomEvent.stopPropagation(e);
    
    console.log('📍 Marker clicked for scheme:', {
      schemeCode: polygonData.schemeCode,
      farmerCode: polygonData.farmerCode,
      chcId: polygonData.chcId,
      cropDemonstrationCode: polygonData.cropDemonstrationCode
    });
    
    // ALWAYS fetch and display details for ANY scheme when marker is clicked
    await handleMarkerClick(polygonData, this, markerLat, markerLng);
  });
  
  return marker;
}, [handleMarkerClick]);

const fetchImplementDetails = async (chcId) => {
  try {
    const token = localStorage.getItem('token');
    const API_URL = process.env.NODE_ENV === "development" 
      ? process.env.REACT_APP_DEV_MODE 
      : process.env.REACT_APP_PRO_MODE;

    console.log(' Fetching implement details for CHC ID:', chcId);

    const config = {
      headers: {
        "accept": "/",
        "Content-Type": "application/json",
        ...(token && { "Authorization": `Bearer ${token}` })
      },
      timeout: 30000
    };

    const payload = {
      chcid: parseInt(chcId),
      userTypeCode: 0
    };

    const response = await axios.post(
      `${API_URL}/api/MT/Get_CHCImpByID`,
      payload,
      config
    );

    console.log(' Implement details response:', response.data);
    return response.data;

  } catch (error) {
    console.error('Error fetching implement details:', error);
    throw error;
  }
};
const formatImplementPopup = (implementData, chcId) => {
  if (!implementData.success || !implementData.dataContent || implementData.dataContent.length === 0) {
    return `
      <div style="text-align: center; padding: 30px;">
        <h6 style="margin-bottom: 15px; color: #dc3545; font-size: 16px;">No Implement Data Available</h6>
        <p style="color: #666;">No implements found for CHC ID: ${chcId}</p>
      </div>
    `;
  }

  let html = `
    
      
      <div style="padding: 16px; max-height: 500px; overflow-y: auto;">
        <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
          <thead>
            <tr style="background: #f8f9fa; border-bottom: 2px solid #dee2e6;">
              <th style="padding: 10px; text-align: left; width: 10%;">S.No.</th>
              <th style="padding: 10px; text-align: left; width: 35%;">Category Name</th>
              <th style="padding: 10px; text-align: left; width: 55%;">Implement Name</th>
            </tr>
          </thead>
          <tbody>
  `;

  let serialNo = 1;
  
  implementData.dataContent.forEach(category => {
    if (category.implements && category.implements.length > 0) {
      category.implements.forEach((implement, index) => {
        html += `
          <tr style="border-bottom: 1px solid #e9ecef; ${index === category.implements.length - 1 ? 'border-bottom: 2px solid #dee2e6;' : ''}">
            <td style="padding: 10px; vertical-align: top; color: #6c757d;">${serialNo}</td>
            <td style="padding: 10px; vertical-align: top; font-weight: 500; color: #495057;">
              ${category.categoryName || 'N/A'}
            </td>
            <td style="padding: 10px; vertical-align: top; color: #212529;">
              <div style="display: flex; align-items: center; gap: 8px;">
                <span>${implement.implementName || 'N/A'}</span>
                ${implement.isWorking !== null ? `
                  <span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-size: 11px; background: ${implement.isWorking ? '#d4edda' : '#f8d7da'}; color: ${implement.isWorking ? '#155724' : '#721c24'};">
                    ${implement.isWorking ? '✓ Working' : '✗ Not Working'}
                  </span>
                ` : ''}
                ${implement.areaCovered ? `
                  <span style="font-size: 11px; color: #6c757d;">
                    (${implement.areaCovered} ha)
                  </span>
                ` : ''}
              </div>
            </td>
          </tr>
        `;
        serialNo++;
      });
    } else {
      html += `
        <tr style="border-bottom: 1px solid #e9ecee;">
          <td style="padding: 10px;">${serialNo}</td>
          <td style="padding: 10px;">${category.categoryName || 'N/A'}</td>
          <td style="padding: 10px; color: #6c757d; font-style: italic;">No implements listed</td>
        </tr>
      `;
      serialNo++;
    }
  });

  

  return html;
};
const handleImplementClick = async (chcId, modalId, contentId) => {
  try {
    const modal = document.getElementById(modalId);
    const content = document.getElementById(contentId);
    
    if (!modal || !content) return;

    // Show loading
    content.innerHTML = '<div style="text-align: center; padding: 20px;"><div class="spinner-border spinner-border-sm text-primary" role="status"><span class="visually-hidden">Loading...</span></div><p style="margin-top: 10px;">Loading implement details...</p></div>';
    modal.style.display = 'block';

    // Use your existing fetchImplementDetails function
    const response = await fetchImplementDetails(chcId);

    if (response && response.dataContent) {
      let html = '<div style="max-width: 600px; background: white; border-radius: 8px; overflow: hidden; box-shadow: 0 10px 40px rgba(0,0,0,0.3);">';
      html += '<div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 12px 16px; display: flex; justify-content: space-between; align-items: center;">';
      html += '<h6 style="margin: 0; color: white; font-size: 14px; font-weight: 600;"> Implement Details - CHC ID: ' + chcId + '</h6>';
      html += '<button onclick="document.getElementById(\'' + modalId + '\').style.display=\'none\'" style="background: transparent; border: none; color: white; font-size: 20px; cursor: pointer;">×</button>';
      html += '</div>';
      html += '<div style="padding: 16px; max-height: 500px; overflow-y: auto;">';
      
      // Format the implement data using your existing formatImplementPopup function
      // or replicate the formatting here
      html += formatImplementPopup(response, chcId).replace(/<div[^>]*>/, '').replace(/<\/div>$/, '');
      
      html += '</div></div>';
      content.innerHTML = html;
    } else {
      content.innerHTML = '<div style="max-width: 400px; margin: 0 auto; background: white; border-radius: 8px; overflow: hidden;"><div style="background: #dc3545; padding: 12px 16px; color: white; font-weight: 600;">Error</div><div style="padding: 40px; text-align: center; color: #721c24; background: #f8d7da;">No implement data available</div></div>';
    }
  } catch (error) {
    console.error('Error in handleImplementClick:', error);
    const content = document.getElementById(contentId);
    if (content) {
      content.innerHTML = '<div style="max-width: 400px; margin: 0 auto; background: white; border-radius: 8px; overflow: hidden;"><div style="background: #dc3545; padding: 12px 16px; color: white; font-weight: 600;">Error</div><div style="padding: 40px; text-align: center; color: #721c24; background: #f8d7da;">Failed to load implement details</div></div>';
    }
  }
};

// Make it globally available
if (typeof window !== 'undefined') {
  window.handleImplementClick = handleImplementClick;
  window.makePopupDraggable = makePopupDraggable;
}

const fetchPolygonData = useCallback(async () => {
  console.log(' START: Fetching Polygon Data');
  
  const schemeCode = getSchemeCode();
  
  if (!schemeCode || !selectedDistrict) {
    clearAllLayers();
    setPolygons([]);
    setError(null);
    return;
  }

  setIsLoading(true);
  setError(null);
  setPolygons([]);

  try {
    const districtCode = selectedDistrict.properties?.Dist_LGD || 
                        selectedDistrict.properties?.districtCode ||
                        selectedDistrict.properties?.DISTRICT_CODE;
    
    let stateCode = selectedDistrict.properties?.STATE_LGD;
    if (!stateCode) {
      stateCode = selectedDistrict.properties?.State_LGD ||
                 selectedDistrict.properties?.stateCode ||
                 selectedDistrict.properties?.STATE_CODE ||
                 selectedDistrict.properties?.st_code;
    }
    
    if (!districtCode) {
      throw new Error('District code not found in district properties');
    }
    
    if (!stateCode) {
      console.warn('⚠️ STATE_LGD not found in district properties, using 0 as fallback');
      stateCode = 0;
    }

    const financialYearParam = getFinancialYearParam();
    const schemeName = getSchemeNameByCode(schemeCode, selectedScheme?.subSchemeCode);
    
    console.log('📡 API Parameters:', {
      schemeCode,
      schemeName,
      stateCode: parseInt(stateCode),
      districtCode: parseInt(districtCode),
      financialYearParam,
      selectedScheme
    });

    let payload;
    let endpoint;
    
    // Handle ATMA scheme (code 20) - special case
    if (parseInt(schemeCode) === 20 && selectedScheme?.subSchemeCode) {
      let demoType;
      const subSchemeName = getSchemeNameByCode(schemeCode, selectedScheme.subSchemeCode);
      
      if (subSchemeName === "(ATMA) SD") {
        demoType = 1;
      } else if (subSchemeName === "(ATMA) CFLD") {
        demoType = 2;
      } else if (subSchemeName === "(ATMA) FLD") {
        demoType = 3;
      } else {
        demoType = parseInt(selectedScheme.subSchemeCode);
      }
      
      endpoint = "/api/Demonstration/getDemonstrationReport";
      payload = {
        schemeCode: 20,
        ...(parseInt(stateCode) !== 0 && { stateCode: parseInt(stateCode) }),
        districtCode: parseInt(districtCode),
        subDistrictCode: null,
        blockCode: null,
        villageCode: null,
        demoType: demoType,
        finYear: financialYearParam === "ALL" ? null : financialYearParam,
        sortField: null,
        pageNumber: 1,
        pageSize: 500, 
        farmerMobile: null,
        cropDemonstrationCode: null
      };
    }
    // Handle ACABC scheme (code 18) - special case
    else if (parseInt(schemeCode) === 18 && selectedScheme?.subSchemeCode) {
      endpoint = "/api/LatLongPolygonList/LatLongPolygonList";
      payload = {
        schemeCode: parseInt(schemeCode),
        finYear: financialYearParam === "ALL" ? null : financialYearParam,
        ...(parseInt(stateCode) !== 0 && { stateCode: parseInt(stateCode) }),
        districtCode: parseInt(districtCode),
        subDistrictCode: 0,
        villageCode: 0,
        subSchemeCode: parseInt(selectedScheme.subSchemeCode)
      };
    }
    // Handle all other schemes
    else {
      endpoint = "/api/LatLongPolygonList/LatLongPolygonList";
      payload = {
        schemeCode: parseInt(schemeCode),
        finYear: financialYearParam === "ALL" ? null : financialYearParam,
        ...(parseInt(stateCode) !== 0 && { stateCode: parseInt(stateCode) }),
        districtCode: parseInt(districtCode),
        subDistrictCode: 0,
        villageCode: 0
      };
    }

    const token = localStorage.getItem('token');
    const API_URL = process.env.NODE_ENV === "development" 
      ? process.env.REACT_APP_DEV_MODE 
      : process.env.REACT_APP_PRO_MODE;

    const config = {
      headers: {
        "Content-Type": "application/json",
        ...(token && { "Authorization": `Bearer ${token}` })
      },
      timeout: 60000
    };

    console.log('🚀 Making API call:', { endpoint, payload });

    const response = await axios.post(
      `${API_URL}${endpoint}`,
      payload,
      config
    );

    console.log('✅ API Response Received:', response.data);

    let polygonData = [];
    
    if (response.data) {
      if (response.data.dataContent && Array.isArray(response.data.dataContent)) {
        polygonData = response.data.dataContent;
        
        // For ATMA, map the fields
        if (parseInt(schemeCode) === 20) {
          polygonData = polygonData.map(item => ({
            ...item,
            farmerCode: item.cropDemonstrationCode,
            surveyorLong: item.longitude,
            surveyorLat: item.latitude,
            finYear: item.financialYear,
            districtName: item.districtName,
            schemeCode: item.schemeCode,
            subSchemeCode: selectedScheme?.subSchemeCode
          }));
        }
        if (parseInt(schemeCode) === 40) {
      console.log('🔧 Fixing scheme 40 data mapping...');
      polygonData = polygonData.map(item => ({
        ...item,
        schemeCode: 40, // Force to 40
        farmerCode: item.chcId || item.farmerCode,
        surveyorLat: item.latitude || item.surveyorLat,
        surveyorLong: item.longitude || item.surveyorLong,
        subSchemeCode: selectedScheme?.subSchemeCode
      }));
      console.log('✅ Fixed scheme 40 data:', polygonData[0]);
    }
  
        // Ensure all items have proper scheme code
        polygonData = polygonData.map(item => ({
          ...item,
          schemeCode: item.schemeCode || parseInt(schemeCode)
        }));
      }
    }
    
    // Filter valid items - keep ALL items that have EITHER polygon OR marker data
    const validItems = polygonData.filter((item) => {
      if (!item) return false;
      
      const hasValidShape = item.polygonShape && 
                           item.polygonShape !== "null" &&
                           item.polygonShape !== "N/A" &&
                           item.polygonShape !== "[]" &&
                           item.polygonShape !== "" &&
                           item.polygonShape !== "{}";
      
      const hasValidMarker = item.surveyorLat && 
                            item.surveyorLong &&
                            parseFloat(item.surveyorLat) !== 0 &&
                            parseFloat(item.surveyorLong) !== 0;
      
      // Keep item if it has EITHER a valid polygon OR valid marker coordinates
      const isValid = hasValidShape || hasValidMarker;
      
      if (!isValid) {
        console.log('❌ Invalid item (no polygon or marker):', {
          polygonShape: item.polygonShape,
          surveyorLat: item.surveyorLat,
          surveyorLong: item.surveyorLong,
          item
        });
      }
      
      return isValid;
    });
    
    setPolygons(validItems);
    
    console.log(`📊 Filtered ${validItems.length} valid items for scheme ${schemeCode}`);
    
    if (onPolygonsLoaded) {
      onPolygonsLoaded(validItems);
    }

    if (validItems.length === 0 && polygonData.length > 0) {
     setError(`Found ${polygonData.length} records but none had valid polygon or location data.`);
    } else if (validItems.length === 0) {
      const districtName = selectedDistrict.properties?.DISTRICT || 'selected district';
      const schemeDisplayName = getSchemeNameByCode(schemeCode, selectedScheme?.subSchemeCode);
      
      let errorMessage = `No data found for ${schemeDisplayName} in ${districtName}`;
      if (financialYearParam && financialYearParam !== "ALL") {
        errorMessage += ` for FY: ${financialYearParam}`;
      }
      
      setError(errorMessage);
    }
  } catch (err) {
    console.error('❌ API Error:', err);
    
    let errorMessage = `Failed to load data: ${err.message}`;
    if (err.response?.data?.message) {
      errorMessage += ` (${err.response.data.message})`;
    }
    
    setError(errorMessage);
setPolygons([]);

    
    if (onPolygonsLoaded) {
      onPolygonsLoaded([]);
    }
  } finally {
    setIsLoading(false);
  }
}, [getSchemeCode, selectedDistrict, selectedFinancialYear, onPolygonsLoaded, getFinancialYearParam, selectedScheme]);

const clearAllLayers = useCallback(() => {
    polygonLayersRef.current.forEach(layer => {
      if (layer && map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    });
    polygonLayersRef.current = [];

    markerLayersRef.current.forEach(layer => {
      if (layer && map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    });
    markerLayersRef.current = [];
  }, [map]);

const addLayersToMap = useCallback(() => {
  console.log('🗺️ Adding layers to map. Total items:', polygons.length);
  
  clearAllLayers();

  if (!polygons.length) {
    console.log('No polygons to display');
    return;
  }

  const newPolygonLayers = [];
  const newMarkerLayers = [];

  polygons.forEach((polygonData, index) => {
    const schemeCodeNum = parseInt(polygonData.schemeCode);
    
    // Check if this item has valid polygon data
    const hasValidPolygon = polygonData.polygonShape && 
                           polygonData.polygonShape !== "null" &&
                           polygonData.polygonShape !== "N/A" &&
                           polygonData.polygonShape !== "[]" &&
                           polygonData.polygonShape !== "" &&
                           polygonData.polygonShape !== "{}";
    
    // Check if this item has valid marker coordinates
    const hasValidMarker = polygonData.surveyorLat && 
                          polygonData.surveyorLong &&
                          parseFloat(polygonData.surveyorLat) !== 0 &&
                          parseFloat(polygonData.surveyorLong) !== 0;
    
    // ALWAYS create a marker if coordinates exist - for EVERY scheme
    if (hasValidMarker) {
      const markerLayer = createSurveyorMarker(polygonData);
      if (markerLayer) {
        markerLayer.addTo(map);
        newMarkerLayers.push(markerLayer);
        console.log(`📍 Added marker ${index + 1}/${polygons.length} for scheme ${schemeCodeNum}`);
      }
    }
    
    // Create polygon only if it has valid shape data
    if (hasValidPolygon) {
      const polygonLayer = createPolygonLayer(polygonData);
      if (polygonLayer) {
        polygonLayer.addTo(map);
        newPolygonLayers.push(polygonLayer);
        console.log(`📐 Added polygon ${index + 1}/${polygons.length} for scheme ${schemeCodeNum}`);
      }
    }
  });

  polygonLayersRef.current = newPolygonLayers;
  markerLayersRef.current = newMarkerLayers;
  
  console.log(`✅ Layers added: ${newPolygonLayers.length} polygons, ${newMarkerLayers.length} markers`);
}, [polygons, map, createPolygonLayer, createSurveyorMarker, clearAllLayers]);

  // Effects
  useEffect(() => {
    if (selectedScheme && selectedDistrict) {
      fetchPolygonData();
    } else {
      clearAllLayers();
      setPolygons([]);
      setError(null);
    }
  }, [selectedScheme, selectedDistrict, selectedFinancialYear, fetchPolygonData]);

  useEffect(() => {
    if (polygons.length > 0) {
      addLayersToMap();
    } else {
      clearAllLayers();
    }
  }, [polygons, addLayersToMap]);

  useEffect(() => {
    return () => {
      clearAllLayers();
    };
  }, [clearAllLayers]);

  const ErrorDisplay = () => {
  if (!error) return null;
  
  return (
    <div className="leaflet-top leaflet-left" style={{ zIndex: 1000 }}>
      <div 
        className="leaflet-control leaflet-bar" 
        style={{ 
          background: 'white', 
          padding: '12px', 
          borderRadius: '6px',
          border: '2px solid #dc3545',
          maxWidth: '450px',
          margin: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <div style={{ color: '#dc3545', fontSize: '18px' }}>
            <i className="bi bi-exclamation-triangle-fill"></i>
          </div>
          <div style={{ flex: 1 }}>
            <strong style={{ color: '#dc3545', fontSize: '14px' }}>No Data Available</strong>
            <div style={{ 
              fontSize: '13px', 
              marginTop: '6px', 
              color: '#666',
              whiteSpace: 'pre-wrap',
              lineHeight: '1.4'
            }}>
              {error}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

  const LoadingDisplay = () => {
    if (!isLoading) return null;
    
    return (
      <div className="leaflet-top leaflet-left" style={{ zIndex: 1000 }}>
        <div 
          className="leaflet-control leaflet-bar" 
          style={{ 
            background: 'white', 
            padding: '12px', 
            borderRadius: '6px',
            border: '2px solid #007bff',
            margin: '10px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div>
              <span style={{ fontSize: '14px', fontWeight: '500' }}>
                Loading survey polygons...
              </span>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                {selectedFinancialYear === "ALL" ? "All financial years" : `FY: ${selectedFinancialYear}`}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const StatsDisplay = () => {
    if (!polygons.length || isLoading || error) return null;
    
    return (
      <div className="leaflet-top leaflet-right" style={{ zIndex: 1000 }}>
        <div 
          className="leaflet-control leaflet-bar" 
          style={{ 
            background: 'white', 
            padding: '10px', 
            borderRadius: '6px',
            border: '2px solid #28a745',
            margin: '10px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ color: '#28a745' }}>
              <i className="bi bi-check-circle-fill"></i>
            </div>
            <div style={{ fontSize: '13px' }}>
              <strong>Loaded:</strong> {polygons.length} polygon{polygons.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <LoadingDisplay />
      <ErrorDisplay />
      <StatsDisplay />
    </>
  );
};

PolygonLayer.propTypes = {
  selectedScheme: PropTypes.shape({
    schemeName: PropTypes.string.isRequired,
    subSchemeCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setShowATMAPopup: PropTypes.func,
  setATMAPopupData: PropTypes.func,
  setShowBeneficiaryModal: PropTypes.func,
  setSelectedBeneficiaryData: PropTypes.func
  }),
  selectedDistrict: PropTypes.object,
  selectedFinancialYear: PropTypes.string,
  onPolygonsLoaded: PropTypes.func,
  schemeColors: PropTypes.object,
  onDistrictSelect: PropTypes.func,
  userVisible: PropTypes.bool,
  setUserVisible: PropTypes.func,
  displayAnalytics: PropTypes.bool,
  setDisplayAnalytics: PropTypes.func,
  setIsSidebarMinimized: PropTypes.func,
};

PolygonLayer.defaultProps = {
  selectedFinancialYear: "",
  schemeColors: {},
  onPolygonsLoaded: () => {},
  setShowATMAPopup: () => {},
  setATMAPopupData: () => {},
  setShowBeneficiaryModal: () => {},
  setSelectedBeneficiaryData: () => {},
  setIsSidebarMinimized: () => {},
};
if (typeof window !== 'undefined') {
  window.makePopupDraggable = makePopupDraggable;
}
export default PolygonLayer;